import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
  Collapse,
  LinearProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AccountBalanceWallet,
  GetApp,
  Refresh,
  Download,
  Upload,
  ExpandMore,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { ethers } from 'ethers';

import { useWallet } from '../hooks/useWallet';
import { useWallet as useSolWallet, useConnection } from '@solana/wallet-adapter-react';
import { useTronWallet } from '../hooks/useTronWallet';
import { Connection } from '@solana/web3.js';
import depositTracker from '../services/depositTracker';
import singleNodeWithdrawal from '../services/singleNodeWithdrawal';
import { WithdrawalServiceECDSA } from '../services/withdrawalServiceECDSA';
import metaMaskOptimizedServiceECDSA from '../services/metaMaskOptimizedECDSA';
import multiTokenTreasuryService from '../services/multiTokenTreasuryService';
import solanaInternullService from '../services/solanaInternullService';
import KeyEncryptionService from '../utils/keyEncryption';
import { prepareKeysForDownload } from '../utils/downloadHelper';
import { processUploadedKeys } from '../utils/uploadHelper';
import PasswordDialog from '../components/PasswordDialog';
import ErrorDialog from '../components/ErrorDialog';
import SuccessDialog from '../components/SuccessDialog';
import KeyRequestDialog from '../components/KeyRequestDialog';
import DkgRequestDialog from '../components/DkgRequestDialog';
import WithdrawNowDialog from '../components/WithdrawNowDialog';
import WithdrawalDialog from '../components/WithdrawalDialog';
import { useThemeMode } from '../hooks/useThemeMode';
import { getChainLogoUrl, getTokenLogoUrl } from '../components/ChainLogos';
import { getMetaMaskProvider } from '../utils/contractInteraction';
import { getChainType, buildExplorerTxUrl } from '../utils/chainDetect';
import useTokenMappings from '../hooks/useTokenMappings';

// DKG Services for V2 keyshare withdrawal
import {
  requestKeysOnly as requestDkgKeysOnly,
  getAvailableDenominations,
  executeRelayedWithdrawal,
  executeSolanaRelayedWithdrawal,
} from '../services/dkgSimpleWithdrawal';
import { getV2Status } from '../services/dkgKeyshareService';

// Relay / Swap services and components
import { getRelayInfo, estimateMaxRelayerFee, estimateRealisticRelayFee } from '../services/relayService';
// WithdrawalModeSelector and RelayStatusTracker are now used inside WithdrawalDialog component

// Backend URL from environment variable
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Create instance of ECDSA withdrawal service
const withdrawalService = new WithdrawalServiceECDSA();


const Dashboard = () => {
  const { isDark } = useThemeMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { connected, account, provider, signer, executeWithPausedPolling } = useWallet();
  const solanaWallet = useSolWallet();
  const { publicKey: solanaPublicKey, connected: solanaConnected } = solanaWallet;
  const { connection: solanaConnection } = useConnection();
  // Tron wallet (TronLink). Used ONLY when the selected deposit is from a Tron chain.
  const tronWallet = useTronWallet();
  const { address: tronAddress, connected: tronConnected, connect: connectTron } = tronWallet;
  const { getValidWithdrawalTargets, loading: mappingsLoading } = useTokenMappings();
  const [deposits, setDeposits] = useState([]);
  const [keyRequestDialog, setKeyRequestDialog] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [denominationRequests, setDenominationRequests] = useState([{ denomination: '', chain: 'sepolia', token: 'ETH' }]);
  const [loading, setLoading] = useState(false);
  const [keyRequestLoading, setKeyRequestLoading] = useState(false);
  const [withdrawDialog, setWithdrawDialog] = useState(false);
  const [withdrawDeposit, setWithdrawDeposit] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [recipientAddress, setRecipientAddress] = useState('');
  const [selectedKeyIndex, setSelectedKeyIndex] = useState(null);
  const [availableKeys, setAvailableKeys] = useState([]);
  // nodeHealth removed - using dkgNodeStatuses for distributed DKG node status
  const [sourceChain, setSourceChain] = useState('sepolia'); // Chain where deposit was made
  const [targetChain, setTargetChain] = useState('sepolia'); // Chain where keys will be used
  const [supportedChains, setSupportedChains] = useState([]); // Chains loaded from backend
  const [availableTokens, setAvailableTokens] = useState([]); // Available tokens for the source chain

  // Key download/upload states
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [passwordDialogMode, setPasswordDialogMode] = useState(''); // 'download' or 'upload'
  const [downloadKeyIndex, setDownloadKeyIndex] = useState(null); // null for all keys, number for specific key

  // Success dialog state (for withdrawal results with tx hash + explorer link)
  const [successDialog, setSuccessDialog] = useState(false);
  const [successDetails, setSuccessDetails] = useState({
    title: '',
    message: '',
    txHash: '',
    explorerUrl: '',
    chainName: '',
  });

  // Error dialog state
  const [errorDialog, setErrorDialog] = useState(false);
  const [errorDetails, setErrorDetails] = useState({
    title: '',
    message: '',
    errorType: '',
    technicalDetails: ''
  });
  const fileInputRef = useRef(null);
  const encryptedDataRef = useRef(null);

  // DKG V2 State
  const [dkgNodeStatuses, setDkgNodeStatuses] = useState([]);
  const [dkgV2Ready, setDkgV2Ready] = useState(false);
  const [dkgDenominations, setDkgDenominations] = useState({}); // Available denominations keyed by chain name
  const [dkgWithdrawalRequests, setDkgWithdrawalRequests] = useState([{ denomination: '', chain: 'sepolia', token: 'ETH' }]);
  const [dkgWithdrawalProgress, setDkgWithdrawalProgress] = useState(null);
  const [dkgRequestDialog, setDkgRequestDialog] = useState(false);
  const [dkgSelectedDeposit, setDkgSelectedDeposit] = useState(null);

  // Withdraw Now (one-click DKG + relay) dialog
  const [withdrawNowDialog, setWithdrawNowDialog] = useState(false);
  const [withdrawNowDeposit, setWithdrawNowDeposit] = useState(null);

  // Collapsible state for withdraw dialog
  const [expandedChains, setExpandedChains] = useState({});
  const [expandedTokens, setExpandedTokens] = useState({});

  // Relay / Gas-Free withdrawal state
  const [withdrawalMode, setWithdrawalMode] = useState('gasfree'); // 'gasfree' | 'swap' — 'direct' (manual) removed from UI; all withdrawals relay
  const [relayInfo, setRelayInfo] = useState(null); // cached relayer info
  const [relayJobId, setRelayJobId] = useState(null); // active relay job ID for tracker
  const [relayNodeUrl, setRelayNodeUrl] = useState(null); // node URL handling the relay
  const [relayerHasBalance, setRelayerHasBalance] = useState(true); // assume true until checked

  useEffect(() => {
    if ((connected && account) || (solanaConnected && solanaPublicKey) || (tronConnected && tronAddress)) {
      // Log wallet changes for debugging
      if (account) {
        console.log('🔄 EVM wallet changed/connected:', account);
      }
      if (solanaPublicKey) {
        console.log('🔄 Solana wallet changed/connected:', solanaPublicKey.toString());
      }
      if (tronAddress) {
        console.log('🔄 Tron wallet changed/connected:', tronAddress);
      }

      // Clear previous wallet's data when switching
      setDeposits([]);
      setSelectedDeposit(null);
      setWithdrawDeposit(null);
      setAvailableKeys([]);

      // Load chains first, then deposits (so verification can use chain info)
      const initializeData = async () => {
        await loadSupportedChains(); // Load chains from backend first
        await loadUserDeposits(); // Then load and verify deposits
      };

      initializeData();
      checkDkgNodes(); // Check DKG V2 nodes
      loadDkgDenominations(); // Load available DKG denominations

      // Fetch relayer info for gas-free withdrawals
      getRelayInfo().then(info => {
        setRelayInfo(info);
        if (info) console.log('Relay info loaded:', info.evm_relayer_address);
      }).catch(err => console.warn('Relay info fetch failed:', err.message));

      // Initialize singleNodeWithdrawal service with Web3 provider
      if (provider) {
        console.log('Initializing singleNodeWithdrawal with provider:', provider);
        singleNodeWithdrawal.initialize(provider.provider || provider)
          .then(() => console.log('✅ singleNodeWithdrawal initialized'))
          .catch(err => console.error('❌ Failed to initialize singleNodeWithdrawal:', err));
      }
    } else {
      // No wallet connected - clear all data
      console.log('🔄 No wallet connected - clearing dashboard data');
      setDeposits([]);
      setSelectedDeposit(null);
      setWithdrawDeposit(null);
      setAvailableKeys([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, account, provider, solanaConnected, solanaPublicKey, tronConnected, tronAddress]);

  // Check relayer on-chain balance when relay info is available
  useEffect(() => {
    if (!relayInfo) return;
    let cancelled = false;

    const checkBalance = async () => {
      try {
        if (relayInfo.evm_relayer_address && provider) {
          const balance = await provider.getBalance(relayInfo.evm_relayer_address);
          if (!cancelled) setRelayerHasBalance(!balance.isZero());
        } else if (relayInfo.solana_relayer_address && solanaConnection) {
          const { PublicKey } = await import('@solana/web3.js');
          const balance = await solanaConnection.getBalance(new PublicKey(relayInfo.solana_relayer_address));
          if (!cancelled) setRelayerHasBalance(balance > 0);
        }
      } catch (e) {
        console.warn('Relayer balance check failed:', e.message);
      }
    };

    checkBalance();
    return () => { cancelled = true; };
  }, [relayInfo, provider, solanaConnection]);

  // checkNodeHealth removed - using checkDkgNodes for distributed DKG node status

  const loadSupportedChains = async () => {
    try {
      const response = await fetch(`${API_URL}/api/cross-chain/chains`);
      const data = await response.json();

      if (data.success && data.chains) {
        console.log('✅ Loaded chains from backend:', data.chains);
        setSupportedChains(data.chains);

        // Set default source and target chains if available
        if (data.chains.length > 0) {
          // Try to find sepolia or localhost as default
          const sepoliaChain = data.chains.find(c => c.chain_name === 'sepolia');
          const localhostChain = data.chains.find(c => c.chain_name === 'localhost');
          const defaultChain = sepoliaChain || localhostChain || data.chains[0];

          setSourceChain(defaultChain.chain_name);
          setTargetChain(defaultChain.chain_name);
        }
      }
    } catch (error) {
      console.error('Failed to load supported chains:', error);
      // Fallback to default chains
      setSupportedChains([
        { chain_name: 'localhost', name: 'Localhost', chain_id: 31337, native_currency: 'ETH' },
        { chain_name: 'sepolia', name: 'Sepolia Testnet', chain_id: 11155111, native_currency: 'ETH' }
      ]);
    }
  };

  // Token config cache for filling in missing token info on existing keys
  const tokenConfigCache = React.useRef({});

  // Fetch token config from backend (with caching)
  const getTokenConfig = async (chainName, tokenSymbol) => {
    const cacheKey = `${chainName}:${tokenSymbol}`;
    if (tokenConfigCache.current[cacheKey]) {
      return tokenConfigCache.current[cacheKey];
    }

    try {
      const response = await fetch(`${API_URL}/api/config/tokens?chain=${chainName}`);
      const data = await response.json();

      if (data.tokens) {
        // Cache all tokens for this chain
        data.tokens.forEach(token => {
          const key = `${chainName}:${token.Symbol}`;
          tokenConfigCache.current[key] = {
            address: token.Address,
            decimals: token.Decimals,
            symbol: token.Symbol
          };
        });
      }

      return tokenConfigCache.current[cacheKey] || null;
    } catch (error) {
      console.warn('Failed to fetch token config:', error);
      return null;
    }
  };

  // Enhance key with missing token info from backend config
  const enhanceKeyWithTokenConfig = async (key) => {
    const chainName = key.chain_name || 'sepolia';
    const tokenSymbol = key.token_symbol || 'ETH';

    // Check if token_address or token_decimals are missing/default
    const needsTokenAddress = !key.token_address || key.token_address === '0x0000000000000000000000000000000000000000';
    const needsTokenDecimals = key.token_decimals === undefined;

    let enhancedKey = { ...key };

    // For non-native tokens (not ETH), try to fetch config
    if ((needsTokenAddress || needsTokenDecimals) && tokenSymbol !== 'ETH') {
      const tokenConfig = await getTokenConfig(chainName, tokenSymbol);
      if (tokenConfig) {
        if (needsTokenAddress) {
          enhancedKey.token_address = tokenConfig.address;
        }
        if (needsTokenDecimals) {
          enhancedKey.token_decimals = tokenConfig.decimals;

          // Recalculate denomination_wei if we just learned the correct decimals
          // and the current value looks like it was calculated with wrong decimals
          if (key.denomination && key.denomination_wei) {
            const correctWei = ethers.utils.parseUnits(key.denomination.toString(), tokenConfig.decimals).toString();
            if (key.denomination_wei !== correctWei) {
              console.log(`🔧 Fixing denomination_wei for ${tokenSymbol}: ${key.denomination_wei} → ${correctWei}`);
              enhancedKey.denomination_wei = correctWei;
            }
          }
        }
      }
    }

    // Default decimals for native token
    if (needsTokenDecimals && tokenSymbol === 'ETH') {
      enhancedKey.token_decimals = 18;
    }

    return enhancedKey;
  };

  // DKG V2 Functions
  const checkDkgNodes = async () => {
    try {
      const statuses = await getV2Status();
      setDkgNodeStatuses(statuses);
      const healthyNodes = statuses.filter(s => s.healthy && s.masterInitialized);
      setDkgV2Ready(healthyNodes.length >= 3);
      console.log(`DKG nodes: ${healthyNodes.length}/5 ready`);
    } catch (err) {
      console.error('Failed to check DKG nodes:', err);
      setDkgV2Ready(false);
    }
  };

  const loadDkgDenominations = async (chainName = 'sepolia') => {
    try {
      const denoms = await getAvailableDenominations(chainName);
      setDkgDenominations(prev => ({ ...prev, [chainName]: denoms }));
      console.log('DKG denominations loaded for', chainName, ':', denoms);
      return denoms;
    } catch (err) {
      console.error('Failed to load DKG denominations:', err);
      return [];
    }
  };

  const handleDkgWithdraw = async (deposit) => {
    setDkgSelectedDeposit(deposit);
    setDkgRequestDialog(true);

    // Resolve chain name from deposit using the same logic as handleRequestKeys
    let chainName = 'sepolia';
    const isSolanaDeposit = getChainType(deposit.userAddress) === 'solana';

    if (isSolanaDeposit) {
      chainName = 'solana-devnet';
    } else if (deposit.chainId && supportedChains.length > 0) {
      const depositChain = supportedChains.find(c => c.chain_id === deposit.chainId || c.chainId === deposit.chainId);
      if (depositChain) {
        chainName = depositChain.chain_name || depositChain.chainName;
      } else {
        chainName = getChainNameFromId(deposit.chainId) || deposit.metadata?.targetChain || 'sepolia';
      }
    } else if (deposit.metadata?.targetChain) {
      // metadata.targetChain could be a string name or a numeric chainId
      const tc = deposit.metadata.targetChain;
      if (typeof tc === 'number' || !isNaN(parseInt(tc))) {
        const resolved = getChainNameFromId(parseInt(tc));
        chainName = resolved || 'sepolia';
      } else {
        chainName = tc;
      }
    }

    console.log('🔗 DKG withdraw - resolved chain:', chainName, 'from chainId:', deposit.chainId, 'metadata.targetChain:', deposit.metadata?.targetChain);

    // Set sourceChain state so executeDkgWithdrawal can use it
    setSourceChain(chainName);

    const token = deposit.metadata?.token || (isSolanaDeposit ? 'SOL' : 'ETH');
    await loadDkgDenominations(chainName);
    await checkDkgNodes();
    // Initialize with one empty request using deposit's chain and token
    setDkgWithdrawalRequests([{ denomination: '', chain: chainName, token: token }]);

    // Preload denominations for other chains in background
    supportedChains.forEach(c => {
      if (c.chain_name !== chainName && !dkgDenominations[c.chain_name]) {
        loadDkgDenominations(c.chain_name);
      }
    });
  };

  // Helper functions for DKG withdrawal requests (similar to single node)
  const addDkgWithdrawalRequest = () => {
    const lastRequest = dkgWithdrawalRequests[dkgWithdrawalRequests.length - 1];
    const defaultChain = lastRequest?.chain || 'sepolia';
    const defaultToken = lastRequest?.token || 'ETH';
    setDkgWithdrawalRequests([...dkgWithdrawalRequests, { denomination: '', chain: defaultChain, token: defaultToken }]);
  };

  const removeDkgWithdrawalRequest = (index) => {
    if (dkgWithdrawalRequests.length > 1) {
      setDkgWithdrawalRequests(dkgWithdrawalRequests.filter((_, i) => i !== index));
    }
  };

  const updateDkgWithdrawalRequest = (index, field, value) => {
    const updated = [...dkgWithdrawalRequests];
    updated[index][field] = value;

    // When chain changes, update token to the chain's native token and load denominations
    if (field === 'chain') {
      updated[index].denomination = ''; // Reset denomination when chain changes
      const selectedChain = supportedChains.find(c => c.chain_name === value);
      if (selectedChain) {
        const chainType = selectedChain.chain_type || 'evm';
        // Set appropriate default token based on chain type
        if (chainType === 'solana') {
          updated[index].token = 'SOL';
        } else {
          updated[index].token = selectedChain.native_currency || 'ETH';
        }
      }
      // Load denominations for the new chain if not cached
      if (!dkgDenominations[value]) {
        loadDkgDenominations(value);
      }
    }

    // Reset denomination when token changes
    if (field === 'token') {
      updated[index].denomination = '';
    }

    setDkgWithdrawalRequests(updated);
  };

  const executeDkgWithdrawal = async () => {
    // Validate withdrawal requests
    const validRequests = dkgWithdrawalRequests
      .filter(req => req.denomination && parseFloat(req.denomination) > 0)
      .map(req => ({
        target_chain: req.chain,
        token_symbol: req.token,
        denomination: req.denomination.toString(),
      }));

    if (validRequests.length === 0) {
      toast.error('Please enter at least one valid denomination');
      return;
    }

    if (!dkgSelectedDeposit) {
      toast.error('Please select a deposit');
      return;
    }

    // Resolve source chain directly from the deposit (don't rely on shared sourceChain state
    // which can be overwritten by other effects like loadSupportedChains).
    // Three-way detection from the deposit's userAddress — this is the SOURCE
    // chain and determines which wallet to trigger (privacy invariant).
    const isSolanaDeposit = getChainType(dkgSelectedDeposit.userAddress) === 'solana';
    const isTronDeposit = getChainType(dkgSelectedDeposit.userAddress) === 'tron';
    let resolvedSourceChain;
    if (isSolanaDeposit) {
      resolvedSourceChain = 'solana-devnet';
    } else if (isTronDeposit) {
      // Pick the Tron chain from supportedChains if available; fallback to 'tron-nile'.
      const depositChain = supportedChains.find(c => c.chain_type === 'tron' || c.chainType === 'tron');
      resolvedSourceChain = depositChain?.chain_name || depositChain?.chainName || 'tron-nile';
    } else if (dkgSelectedDeposit.chainId && supportedChains.length > 0) {
      const depositChain = supportedChains.find(c => c.chain_id === dkgSelectedDeposit.chainId || c.chainId === dkgSelectedDeposit.chainId);
      if (depositChain) {
        resolvedSourceChain = depositChain.chain_name || depositChain.chainName;
      } else {
        resolvedSourceChain = getChainNameFromId(dkgSelectedDeposit.chainId) || dkgSelectedDeposit.metadata?.targetChain || 'sepolia';
      }
    } else if (dkgSelectedDeposit.metadata?.targetChain) {
      const tc = dkgSelectedDeposit.metadata.targetChain;
      if (typeof tc === 'number' || !isNaN(parseInt(tc))) {
        resolvedSourceChain = getChainNameFromId(parseInt(tc)) || 'sepolia';
      } else {
        resolvedSourceChain = tc;
      }
    } else {
      resolvedSourceChain = 'sepolia';
    }

    // CHAIN-ISOLATION: validate ONLY the wallet for this deposit's source chain.
    // Never fall back to "whichever wallet is connected."
    if (isSolanaDeposit) {
      if (!solanaConnected || !solanaPublicKey) {
        toast.error('Please connect your Phantom wallet for Solana deposits');
        return;
      }
      const currentAddress = solanaPublicKey.toString();
      if (currentAddress.toLowerCase() !== dkgSelectedDeposit.userAddress.toLowerCase()) {
        toast.error(`Wallet mismatch! Deposit was made with ${dkgSelectedDeposit.userAddress} but you're connected with ${currentAddress}`);
        return;
      }
    } else if (isTronDeposit) {
      // Tron deposit → TronLink only. Never touch MetaMask or Phantom.
      if (!tronConnected || !tronAddress) {
        try { await connectTron(); } catch (e) {
          toast.error('Please connect TronLink for this Tron deposit');
          return;
        }
      }
      // Verify Tron wallet matches the deposit (case-sensitive — Tron base58)
      const currentAddress = tronAddress || tronWallet.address;
      if (currentAddress && currentAddress !== dkgSelectedDeposit.userAddress) {
        toast.error(`Wallet mismatch! Deposit was made with ${dkgSelectedDeposit.userAddress} but you're connected with ${currentAddress}`);
        return;
      }
    } else {
      // EVM deposit → MetaMask only.
      if (!signer) {
        toast.error('Please connect your MetaMask wallet first');
        return;
      }
      const currentAddress = await signer.getAddress();
      if (currentAddress.toLowerCase() !== dkgSelectedDeposit.userAddress.toLowerCase()) {
        toast.error(`Wallet mismatch! Deposit was made with ${dkgSelectedDeposit.userAddress} but you're connected with ${currentAddress}`);
        return;
      }
    }

    // Validate total requested doesn't exceed deposit
    const totalRequested = validRequests.reduce((sum, req) => sum + parseFloat(req.denomination), 0);
    if (totalRequested > dkgSelectedDeposit.amount) {
      toast.error(`Total requested (${totalRequested}) exceeds deposit amount (${dkgSelectedDeposit.amount})`);
      return;
    }

    // Frontend validation: Check token/denomination availability before sending to backend
    // This reduces unnecessary backend requests for invalid combinations
    for (const req of validRequests) {
      // Check if denomination is available for the target chain/token
      const chainDenoms = dkgDenominations[req.target_chain] || [];
      const matchingDenom = chainDenoms.find(d =>
        d.denomination === req.denomination &&
        (d.tokenSymbol === req.token_symbol || (d.tokenSymbol === 'ETH' && req.token_symbol === 'ETH'))
      );

      if (!matchingDenom && chainDenoms.length > 0) {
        // Only warn if we have denominations loaded - backend will do final validation
        console.warn(`Frontend validation: Denomination ${req.denomination} ${req.token_symbol} may not be available`);
      }

      // Check if token is configured for the target chain
      // Backend returns supported_tokens as array of symbol strings: ["USDC", "ETH"]
      const targetChainConfig = supportedChains.find(c => c.chain_name === req.target_chain);
      if (targetChainConfig) {
        const supportedTokens = targetChainConfig.supported_tokens || [];
        const nativeCurrency = targetChainConfig.native_currency || 'ETH';
        const isNativeToken = req.token_symbol === nativeCurrency || req.token_symbol === 'ETH';
        const isConfiguredToken = supportedTokens.includes(req.token_symbol);

        if (!isNativeToken && !isConfiguredToken && supportedTokens.length > 0) {
          toast.error(`Token ${req.token_symbol} is not configured for ${req.target_chain}. Available: ${nativeCurrency}${supportedTokens.length > 0 ? ', ' + supportedTokens.join(', ') : ''}`);
          return;
        }
      }
    }

    setKeyRequestLoading(true);
    setDkgWithdrawalProgress({ step: 'starting', message: 'Starting DKG key request...' });

    try {
      // Compute occurrence offset: number of keys already claimed for this deposit
      // This tells backend nodes where to start numbering new keys, avoiding race conditions
      const existingDeposit = depositTracker.getDepositByTxHash(dkgSelectedDeposit.txHash);
      const existingKeysCount = existingDeposit?.keys?.length || 0;

      // Request keys from DKG nodes (no withdrawal - just key collection).
      // ONLY the wallet matching the source chain is passed in; all others null.
      const result = await requestDkgKeysOnly({
        depositTxHash: dkgSelectedDeposit.txHash.trim(),
        sourceChain: resolvedSourceChain,
        withdrawalRequests: validRequests,
        signer: (isSolanaDeposit || isTronDeposit) ? null : signer,
        solanaWallet: isSolanaDeposit ? solanaWallet : null,
        tronWallet: isTronDeposit ? tronWallet : null,
        isSolana: isSolanaDeposit,
        isTron: isTronDeposit,
        occurrenceOffset: existingKeysCount,
      }, (progress) => {
        console.log('DKG Progress:', progress);
        setDkgWithdrawalProgress(progress);
      });

      if (result.success) {
        // Check if deposit is fully claimed or partially claimed
        // Use multiple signals to determine remaining: the reported remaining deposit,
        // AND a comparison of claimed vs deposit amount (guards against stale/bad node data)
        const existingDeposit = depositTracker.getDepositByTxHash(dkgSelectedDeposit.txHash);
        const existingKeys = existingDeposit?.keys || [];
        const allKeys = [...existingKeys, ...result.keys];

        const totalKeyValue = allKeys.reduce((sum, k) => sum + parseFloat(k.denomination || 0), 0);
        const hasRemaining = result.remainingDeposit > 0.000001 ||
          (result.depositAmount > 0 && result.claimedAmount < result.depositAmount - 0.000001) ||
          (dkgSelectedDeposit.amount > 0 && totalKeyValue < dkgSelectedDeposit.amount - 0.000001);

        // Compute the best remaining deposit value:
        // Prefer backend-reported value if positive, otherwise compute from deposit amount and keys
        const effectiveDepositAmount = result.depositAmount || dkgSelectedDeposit.amount || 0;
        const computedRemaining = Math.max(0, effectiveDepositAmount - totalKeyValue);
        const effectiveRemaining = result.remainingDeposit > 0 ? result.remainingDeposit : computedRemaining;

        if (hasRemaining) {
          // Partial claim: keep deposit in "confirmed" so user can claim more keys
          depositTracker.updateDepositStatus(dkgSelectedDeposit.txHash, 'confirmed', {
            requestId: existingDeposit?.requestId || `dkg-${Date.now()}`,
            keys: allKeys,
            total_value: totalKeyValue.toString(),
            claimedAmount: totalKeyValue,
            remainingDeposit: effectiveRemaining,
            depositAmount: effectiveDepositAmount,
            keysReceived: true,
          });
        } else {
          // Fully claimed: move to "keys_received"
          depositTracker.updateDepositStatus(dkgSelectedDeposit.txHash, 'keys_received', {
            requestId: existingDeposit?.requestId || `dkg-${Date.now()}`,
            keys: allKeys,
            total_value: totalKeyValue.toString(),
            claimedAmount: totalKeyValue,
            remainingDeposit: 0,
            depositAmount: effectiveDepositAmount,
            keysReceived: true,
          });
        }

        // Get unique tokens from withdrawal requests for the success message
        const uniqueTokens = [...new Set(validRequests.map(r => r.token_symbol))];
        const tokensStr = uniqueTokens.join('/');

        const remainingMsg = hasRemaining
          ? ` (${result.remainingDeposit} remaining to claim)`
          : ' (deposit fully claimed)';
        toast.success(`DKG Keys received! Got ${result.keys.length} key(s) totaling ${result.total_value} ${tokensStr}${remainingMsg}`);

        setDkgRequestDialog(false);
        setDkgSelectedDeposit(null);
        setDkgWithdrawalProgress(null);
        setDkgWithdrawalRequests([{ denomination: '', chain: 'sepolia', token: 'ETH' }]);
        loadUserDeposits(); // Refresh
      } else {
        // Use error dialog for DKG errors — classified and user-friendly
        const errorCode = result.errorCode || 'UNKNOWN';
        const errorTitles = {
          INSUFFICIENT_BALANCE: 'Deposit Fully Claimed',
          BALANCE_RECORDING_FAILED: 'Temporary Error',
          KEY_ALREADY_USED: 'Key Already Used',
          DEPOSIT_NOT_FOUND: 'Deposit Not Found',
          SIGNATURE_INVALID: 'Signature Verification Failed',
          NO_KEYS_AVAILABLE: 'No Keys Available',
          INVALID_DENOMINATION: 'Invalid Denomination',
          NODE_UNREACHABLE: 'DKG Nodes Unreachable',
        };
        setErrorDetails({
          title: errorTitles[errorCode] || 'Key Request Failed',
          message: result.error,
          errorType: errorCode,
          technicalDetails: result.errorTechnical || result.error,
        });
        setErrorDialog(true);
      }
    } catch (err) {
      console.error('DKG key request error:', err);
      const errorCode = err.code || 'UNKNOWN';
      const errorTitles = {
        INSUFFICIENT_BALANCE: 'Deposit Fully Claimed',
        BALANCE_RECORDING_FAILED: 'Temporary Error',
        KEY_ALREADY_USED: 'Key Already Used',
        DEPOSIT_NOT_FOUND: 'Deposit Not Found',
        SIGNATURE_INVALID: 'Signature Verification Failed',
        NO_KEYS_AVAILABLE: 'No Keys Available',
        INVALID_DENOMINATION: 'Invalid Denomination',
        NODE_UNREACHABLE: 'DKG Nodes Unreachable',
      };
      setErrorDetails({
        title: errorTitles[errorCode] || 'Key Request Failed',
        message: err.message,
        errorType: errorCode,
        technicalDetails: err.technical || err.message,
      });
      setErrorDialog(true);
    } finally {
      setKeyRequestLoading(false);
    }
  };

  // ---------- Withdraw Now (one-click DKG + relay) ----------

  const handleWithdrawNow = async (deposit) => {
    setWithdrawNowDeposit(deposit);
    setWithdrawNowDialog(true);

    // Resolve source chain and preload denominations (same logic as handleDkgWithdraw)
    const isSolanaDeposit = getChainType(deposit.userAddress) === 'solana';
    let chainName = 'sepolia';
    if (isSolanaDeposit) {
      chainName = 'solana-devnet';
    } else if (deposit.chainId && supportedChains.length > 0) {
      const depositChain = supportedChains.find(c => c.chain_id === deposit.chainId || c.chainId === deposit.chainId);
      if (depositChain) chainName = depositChain.chain_name || depositChain.chainName;
      else chainName = getChainNameFromId(deposit.chainId) || deposit.metadata?.targetChain || 'sepolia';
    } else if (deposit.metadata?.targetChain) {
      const tc = deposit.metadata.targetChain;
      chainName = (typeof tc === 'number' || !isNaN(parseInt(tc))) ? (getChainNameFromId(parseInt(tc)) || 'sepolia') : tc;
    }
    setSourceChain(chainName);
    await loadDkgDenominations(chainName);
    await checkDkgNodes();
    supportedChains.forEach(c => {
      if (c.chain_name !== chainName && !dkgDenominations[c.chain_name]) loadDkgDenominations(c.chain_name);
    });
  };

  const executeWithdrawNow = async ({ deposit, requests, onProgress, onKeysReady, onWithdrawStatus, onDone, onError }) => {
    // Three-way detection — wallet routing follows the source chain only.
    const isSolanaDeposit = getChainType(deposit.userAddress) === 'solana';
    const isTronDeposit = getChainType(deposit.userAddress) === 'tron';

    // Resolve source chain
    let resolvedSourceChain = 'sepolia';
    if (isSolanaDeposit) {
      resolvedSourceChain = 'solana-devnet';
    } else if (isTronDeposit) {
      const depositChain = supportedChains.find(c => c.chain_type === 'tron' || c.chainType === 'tron');
      resolvedSourceChain = depositChain?.chain_name || depositChain?.chainName || 'tron-nile';
    } else if (deposit.chainId && supportedChains.length > 0) {
      const depositChain = supportedChains.find(c => c.chain_id === deposit.chainId || c.chainId === deposit.chainId);
      if (depositChain) resolvedSourceChain = depositChain.chain_name || depositChain.chainName;
      else resolvedSourceChain = getChainNameFromId(deposit.chainId) || deposit.metadata?.targetChain || 'sepolia';
    }

    // Validate wallet — chain-isolated, no fallback.
    if (isSolanaDeposit) {
      if (!solanaConnected || !solanaPublicKey) { onError('Please connect your Phantom wallet'); return; }
      if (solanaPublicKey.toString().toLowerCase() !== deposit.userAddress.toLowerCase()) { onError('Wallet mismatch'); return; }
    } else if (isTronDeposit) {
      if (!tronConnected || !tronAddress) {
        try { await connectTron(); } catch (e) { onError('Please connect TronLink for this Tron deposit'); return; }
      }
      if (tronAddress && tronAddress !== deposit.userAddress) { onError('Wallet mismatch'); return; }
    } else {
      if (!signer) { onError('Please connect your MetaMask wallet'); return; }
      const addr = await signer.getAddress();
      if (addr.toLowerCase() !== deposit.userAddress.toLowerCase()) { onError('Wallet mismatch'); return; }
    }

    // Build withdrawal requests
    const validRequests = requests.map(r => ({
      target_chain: r.chain,
      token_symbol: r.token,
      denomination: r.denomination.toString(),
    }));

    const existingDeposit = depositTracker.getDepositByTxHash(deposit.txHash);
    const existingKeysCount = existingDeposit?.keys?.length || 0;

    try {
      // Step 1: Request keys from DKG
      onProgress({ step: 'signing', message: 'Sign the proof message in your wallet...' });

      const result = await requestDkgKeysOnly({
        depositTxHash: deposit.txHash.trim(),
        sourceChain: resolvedSourceChain,
        withdrawalRequests: validRequests,
        signer: (isSolanaDeposit || isTronDeposit) ? null : signer,
        solanaWallet: isSolanaDeposit ? solanaWallet : null,
        tronWallet: isTronDeposit ? tronWallet : null,
        isSolana: isSolanaDeposit,
        isTron: isTronDeposit,
        occurrenceOffset: existingKeysCount,
      }, onProgress);

      if (!result.success) {
        onError(result.error || 'Key request failed');
        return;
      }

      // Save keys to deposit tracker
      const allKeys = [...(existingDeposit?.keys || []), ...result.keys];
      depositTracker.updateDepositStatus(deposit.txHash, 'keys_received', {
        requestId: existingDeposit?.requestId || `dkg-${Date.now()}`,
        keys: allKeys,
      });

      onKeysReady();

      // Step 2: Relay withdraw each key
      const statuses = result.keys.map((_, i) => ({
        status: 'pending', error: null, txHash: null,
      }));
      onWithdrawStatus([...statuses]);

      if (!relayInfo) {
        // No relay available — save keys but can't auto-withdraw
        statuses.forEach((s, i) => { s.status = 'failed'; s.error = 'Relay not available'; });
        onWithdrawStatus([...statuses]);
        onDone();
        loadUserDeposits();
        return;
      }

      for (let i = 0; i < result.keys.length; i++) {
        statuses[i].status = 'withdrawing';
        onWithdrawStatus([...statuses]);

        try {
          const key = result.keys[i];
          const recipient = requests[i].recipientAddress;
          const enrichedKey = await enhanceKeyWithTokenConfig(key);
          const isSolana = !!(key.chain_name?.toLowerCase().includes('solana') || key.solana_address);
          const isNative = !key.token_address || key.token_address === '0x0000000000000000000000000000000000000000';

          let relayResult;
          if (isSolana) {
            const denomBaseUnits = key.denomination_wei
              ? BigInt(key.denomination_wei)
              : BigInt(Math.floor(parseFloat(key.denomination) * 1e9));
            const tenPercent = denomBaseUnits / 10n;
            const minCap = 10_000_000n;
            const maxFee = (tenPercent > minCap ? tenPercent : minCap).toString();
            relayResult = await executeSolanaRelayedWithdrawal({
              key: enrichedKey, recipientAddress: recipient, relayInfo,
              maxRelayerFee: maxFee, nodeUrl: relayInfo.nodeUrl,
              isSwap: false, targetAddress: null,
            });
          } else {
            const maxFee = estimateMaxRelayerFee(relayInfo, key.denomination_wei, isNative);
            relayResult = await executeRelayedWithdrawal({
              key: enrichedKey, recipientAddress: recipient, relayInfo,
              maxRelayerFee: maxFee, nodeUrl: relayInfo.nodeUrl,
              isSwap: false, targetAddress: null,
            });
          }

          if (relayResult.success) {
            statuses[i] = { status: 'success', txHash: relayResult.txHash, error: null };
            // Persist withdrawal
            const persisted = depositTracker.getDepositByTxHash(deposit.txHash);
            const existingWithdrawals = persisted?.withdrawals || [];
            depositTracker.updateDepositStatus(deposit.txHash, 'keys_received', {
              keys: allKeys.map(k => (k.key_index ?? k.keyIndex) === (key.key_index ?? key.keyIndex)
                ? { ...k, isUsed: true, withdrawalTxHash: relayResult.txHash, withdrawalRecipient: recipient }
                : k),
              withdrawals: [...existingWithdrawals, {
                txHash: relayResult.txHash, timestamp: Date.now(), recipient,
                keyIndex: key.key_index ?? key.keyIndex, chainName: key.chain_name,
                tokenSymbol: key.token_symbol, amount: key.denomination,
              }],
            });
          } else {
            throw new Error(relayResult.error || 'Relay failed');
          }
        } catch (err) {
          console.error(`WithdrawNow: key ${i} relay failed:`, err);
          statuses[i] = { status: 'failed', error: err.message, txHash: null };
        }
        onWithdrawStatus([...statuses]);
      }

      onDone();
      loadUserDeposits();
    } catch (err) {
      console.error('WithdrawNow error:', err);
      onError(err.message);
    }
  };

  // Helper to get block explorer URL for a chain name
  const getExplorerUrl = (chainName, txHash) => buildExplorerTxUrl(chainName, txHash, supportedChains);

  // Helper functions for chain and token logos
  // Helper to map chain ID to chain name - uses backend data (no hardcoding!)
  const getChainNameFromId = (chainId) => {
    // Look up chain name from backend-provided supportedChains
    const chain = supportedChains.find(c => c.chain_id === chainId || c.chainId === chainId);
    return chain ? (chain.chain_name || chain.chainName) : null;
  };

  // Helper to get display name for chain (prioritizes backend data)
  const getChainDisplayName = (chainName) => {
    if (!chainName) return 'Unknown Chain';

    // Short display names — strip testnet/devnet suffixes since the app
    // already indicates this is a testnet environment
    const shortNames = {
      'ethereum': 'Ethereum',
      'sepolia': 'Ethereum',
      'polygon': 'Polygon',
      'polygon-amoy': 'Polygon',
      'bsc': 'BNB Chain',
      'bsc-testnet': 'BNB Chain',
      'base-sepolia': 'Base',
      'base': 'Base',
      'hyperliquid': 'Hyperliquid',
      'solana-devnet': 'Solana',
      'solana': 'Solana',
    };

    if (shortNames[chainName]) return shortNames[chainName];

    // Try backend supportedChains, then strip common suffixes
    const chain = supportedChains.find(c => c.chain_name === chainName || c.name === chainName);
    const raw = chain?.name || chain?.chain_name || chainName;
    return raw
      .replace(/\s*(Sepolia|Testnet|Devnet|Amoy|Goerli)\s*/gi, '')
      .trim() || raw;
  };

  // Alias imported logo helpers for local use
  const getChainLogo = getChainLogoUrl;
  const getTokenLogo = getTokenLogoUrl;

  // Toggle functions for collapsible sections
  const toggleChain = (chainName) => {
    setExpandedChains(prev => ({
      ...prev,
      [chainName]: !prev[chainName]
    }));
  };

  const toggleToken = (chainName, tokenSymbol) => {
    const key = `withdraw-${chainName}-${tokenSymbol}`;
    setExpandedTokens(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const loadUserDeposits = async () => {
    try {
      setLoading(true);

      // Load deposits from both EVM and Solana wallets
      let allDeposits = [];

      // Load EVM deposits if connected
      if (account) {
        const evmDeposits = depositTracker.getUserDeposits(account);
        allDeposits = [...evmDeposits];
        console.log(`📊 Loaded ${evmDeposits.length} EVM deposits for ${account}`);
      }

      // Load Solana deposits if connected
      if (solanaPublicKey) {
        const solanaAddress = solanaPublicKey.toString();
        const solanaDeposits = depositTracker.getUserDeposits(solanaAddress);
        allDeposits = [...allDeposits, ...solanaDeposits];
        console.log(`📊 Loaded ${solanaDeposits.length} Solana deposits for ${solanaAddress}`);
      }

      // Load Tron deposits if connected
      if (tronAddress) {
        const tronDeposits = depositTracker.getUserDeposits(tronAddress);
        allDeposits = [...allDeposits, ...tronDeposits];
        console.log(`📊 Loaded ${tronDeposits.length} Tron deposits for ${tronAddress}`);
      }

      const userDeposits = allDeposits;

      // Migration: Fix old deposits missing metadata by inferring from chainId
      for (const deposit of userDeposits) {
        if (!deposit.metadata || !deposit.metadata.token) {
          console.log('🔧 Migrating old deposit to add token metadata:', deposit.txHash);

          // Infer token from chainId
          let token = 'ETH';
          let tokenAddress = '0x0000000000000000000000000000000000000000';

          if (deposit.chainId === 97) {
            // BNB Testnet
            token = 'tBNB';
          } else if (deposit.chainId === 56) {
            // BNB Mainnet
            token = 'BNB';
          }
          // Add more chain mappings as needed

          depositTracker.updateDepositStatus(deposit.txHash, deposit.status, {
            metadata: {
              token: token,
              tokenAddress: tokenAddress,
              crossChain: deposit.metadata?.crossChain || false,
              targetChain: deposit.metadata?.targetChain || deposit.chainId
            }
          });
        }
      }

      // Reload deposits after migration
      let updatedDeposits = [];
      if (account) {
        updatedDeposits = [...updatedDeposits, ...depositTracker.getUserDeposits(account)];
      }
      if (solanaPublicKey) {
        updatedDeposits = [...updatedDeposits, ...depositTracker.getUserDeposits(solanaPublicKey.toString())];
      }
      if (tronAddress) {
        updatedDeposits = [...updatedDeposits, ...depositTracker.getUserDeposits(tronAddress)];
      }

      // PERFORMANCE OPTIMIZATION: Removed automatic RPC verification on page load
      // Key status is now verified on-demand when user clicks "Withdraw" button
      // This prevents hitting RPC for every deposit on page load, improving load time
      // Keys are verified in handleWithdraw() function before withdrawal dialog opens

      // Fix: Restore deposits that were incorrectly moved to 'keys_received' or 'withdrawn'
      // while they still have remaining balance. This happens when all requested keys are
      // withdrawn but the deposit still has unclaimed balance (e.g., deposited 1 ETH, claimed
      // 0.01 ETH key, withdrew it → status became 'withdrawn' but 0.99 ETH remains unclaimed).
      updatedDeposits.forEach(deposit => {
        if ((deposit.status === 'keys_received' || deposit.status === 'withdrawn') && deposit.keys?.length > 0 && deposit.amount > 0) {
          const totalKeyValue = deposit.keys.reduce((sum, k) => sum + parseFloat(k.denomination || 0), 0);
          if (totalKeyValue < deposit.amount - 0.000001) {
            console.log(`Restoring deposit ${deposit.txHash?.slice(0, 16)} to confirmed (keys=${totalKeyValue}, deposit=${deposit.amount})`);
            depositTracker.updateDepositStatus(deposit.txHash, 'confirmed', {
              remainingDeposit: deposit.amount - totalKeyValue,
              claimedAmount: totalKeyValue,
              depositAmount: deposit.amount,
            });
            deposit.status = 'confirmed';
            deposit.remainingDeposit = deposit.amount - totalKeyValue;
            deposit.claimedAmount = totalKeyValue;
          }
        }
      });

      setDeposits(updatedDeposits);

      // Mark keys as used based on LOCAL withdrawal history
      updatedDeposits
        .filter(d => d.status !== 'failed')
        .forEach(deposit => {
          if (deposit.withdrawals && deposit.withdrawals.length > 0) {
            deposit.withdrawals.forEach(withdrawal => {
              if (withdrawal.keyIndex === undefined || withdrawal.keyIndex === null) return;
              const withdrawnKey = deposit.keys?.find(k =>
                k.keyIndex === withdrawal.keyIndex || k.key_index === withdrawal.keyIndex
              );
              if (withdrawnKey) {
                withdrawnKey.isUsed = true;
                console.log(`📝 Key #${withdrawnKey.keyIndex ?? withdrawnKey.key_index} marked as used from local history`);
              }
            });
          }
        });

      // Check confirmations for pending deposits — route by chain type so
      // each deposit hits the right RPC (Tron tx hashes are NOT valid Solana
      // tx hashes and would error if mis-routed).
      for (const deposit of updatedDeposits) {
        if (deposit.status === 'pending') {
          try {
            const depositType = getChainType(deposit.userAddress);

            if (depositType === 'solana' && solanaConnection) {
              console.log(`Checking Solana deposit confirmation: ${deposit.txHash}`);
              await depositTracker.checkSolanaDepositConfirmation(solanaConnection, deposit.txHash);
            } else if (depositType === 'tron') {
              console.log(`Checking Tron deposit confirmation: ${deposit.txHash}`);
              // Tron uses TronGrid HTTP directly — no wallet provider needed.
              const tronChain = supportedChains.find((c) => c.chain_type === 'tron' || c.chainType === 'tron');
              await depositTracker.checkTronDepositConfirmation(deposit.txHash, tronChain?.rpc_url || tronChain?.rpcUrl);
            } else if (depositType === 'evm' && provider) {
              console.log(`Checking EVM deposit confirmation: ${deposit.txHash}`);
              await depositTracker.checkDepositConfirmation(provider, deposit.txHash);
            }
          } catch (error) {
            console.error('Error checking confirmation:', error);
          }
        }
      }

      // Reload to get updated statuses (e.g. after the per-chain confirmation
      // checks above flipped a deposit from 'pending' → 'confirmed').
      let finalDeposits = [];
      if (account) {
        finalDeposits = [...finalDeposits, ...depositTracker.getUserDeposits(account)];
      }
      if (solanaPublicKey) {
        finalDeposits = [...finalDeposits, ...depositTracker.getUserDeposits(solanaPublicKey.toString())];
      }
      if (tronAddress) {
        finalDeposits = [...finalDeposits, ...depositTracker.getUserDeposits(tronAddress)];
      }
      setDeposits(finalDeposits);

    } catch (error) {
      console.error('Error loading deposits:', error);
      toast.error('Failed to load deposits');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for managing denomination requests
  const addDenominationRequest = () => {
    const lastRequest = denominationRequests[denominationRequests.length - 1];
    const defaultChain = lastRequest ? lastRequest.chain : (targetChain || 'sepolia');
    const defaultToken = lastRequest ? lastRequest.token : (selectedDeposit?.metadata?.token || 'ETH');
    setDenominationRequests([...denominationRequests, { denomination: '', chain: defaultChain, token: defaultToken }]);
  };

  const removeDenominationRequest = (index) => {
    if (denominationRequests.length > 1) {
      setDenominationRequests(denominationRequests.filter((_, i) => i !== index));
    }
  };

  const updateDenominationRequest = (index, field, value) => {
    const updated = [...denominationRequests];
    updated[index][field] = value;
    setDenominationRequests(updated);
  };

  const handleWithdraw = async (deposit, specificKeyIndex = null) => {
    console.log('🔑 handleWithdraw called for deposit:', {
      txHash: deposit.txHash,
      amount: deposit.amount,
      token: deposit.metadata?.token,
      requestId: deposit.requestId,
      keysCount: deposit.keys?.length || 0,
      specificKeyIndex: specificKeyIndex
    });

    if (!deposit.requestId) {
      // Generate a requestId if missing (e.g. from partial claims saved before fix)
      deposit.requestId = `dkg-${Date.now()}`;
      console.log('Generated missing requestId:', deposit.requestId);
    }

    if (!deposit.keys && !deposit.keyshares) {
      toast.error('No keys found for this deposit');
      return;
    }

    setWithdrawDeposit(deposit);

    // Detect chain family from the first key. Tron uses chain_name "tron-*" and
    // an EVM-shaped ethereum_address; Solana uses chain_name "solana*" or sets
    // solana_address.
    const firstKey = deposit.keys && deposit.keys.length > 0 ? deposit.keys[0] : null;
    const isSolanaKeys = firstKey &&
      (firstKey.chain_name?.toLowerCase().startsWith('solana') || firstKey.solana_address);
    const isTronKeys = firstKey &&
      (firstKey.chain_name?.toLowerCase().startsWith('tron') || firstKey.chain_type === 'tron');

    // Set recipient address based on chain type
    if (isSolanaKeys && solanaPublicKey) {
      setRecipientAddress(solanaPublicKey.toString());
    } else if (isTronKeys && tronAddress) {
      setRecipientAddress(tronAddress);
    } else if (account) {
      setRecipientAddress(account);
    }

    // Get available keys - handle both single-node keys and multi-node keyshares
    let keys = [];
    if (deposit.keys) {
      // Detect if these are Solana / Tron keys
      const isSolanaKeySet = deposit.keys.length > 0 &&
        (deposit.keys[0].chain_name?.toLowerCase().includes('solana') || deposit.keys[0].solana_address);
      const isTronKeySet = deposit.keys.length > 0 &&
        (deposit.keys[0].chain_name?.toLowerCase().startsWith('tron') || deposit.keys[0].chain_type === 'tron');

      let checkResults;

      if (isTronKeySet) {
        // Tron keys: skip EVM JSON-RPC; check the contract's nullifiers
        // mapping via TronGrid's triggerconstantcontract HTTP endpoint.
        toast.info('Checking key status on Tron...');
        try {
          const { checkMultipleTronKeys, tronGridHostFor } = await import('../services/tronTreasuryService');
          const chainName = deposit.keys[0].chain_name || 'tron-nile';
          const backendRpc = multiTokenTreasuryService.getRpcUrlByChainName(chainName);
          const tronGridHost = tronGridHostFor(chainName, backendRpc);
          const tronKeysToCheck = deposit.keys.map(key => ({
            treasury_address: key.treasury_address || key.treasuryAddress,
            merkle_root_id: key.merkle_root_id ?? key.merkleRootId,
            tree_index: key.tree_index !== undefined ? key.tree_index : key.treeIndex,
          }));
          checkResults = await checkMultipleTronKeys({ tronGridHost, keysToCheck: tronKeysToCheck });
        } catch (e) {
          console.warn('Tron nullifier check failed, defaulting to available:', e.message);
          checkResults = deposit.keys.map(() => ({ isUsed: false, success: false }));
        }
      } else if (isSolanaKeySet) {
        // Solana keys: Skip EVM key checking — check via Solana connection instead
        toast.info('Checking key status on Solana...');

        // Try to check key usage on Solana if connection available
        let solanaConn = solanaConnection;
        const backendRpc = multiTokenTreasuryService.getRpcUrlByChainName(deposit.keys[0].chain_name || 'solana-devnet');
        if (backendRpc) {
          solanaConn = new Connection(backendRpc, 'confirmed');
        }

        if (solanaConn) {
          try {
            // Check each key's usage on Solana by querying the merkle root account
            checkResults = await Promise.all(deposit.keys.map(async (key) => {
              try {
                const treeIdx = key.tree_index !== undefined ? key.tree_index : key.treeIndex;
                console.log(`[Solana key check] key_index=${key.key_index} tree_index=${treeIdx} merkle_root_id=${key.merkle_root_id} merkle_root=${key.merkle_root?.slice(0, 16)}...`);
                const used = await solanaInternullService.isKeyUsedOnChain(
                  solanaConn,
                  key.merkle_root,
                  treeIdx,
                  key.merkle_root_id
                );
                console.log(`[Solana key check] key_index=${key.key_index} → isUsed=${used}`);
                return { isUsed: used, success: true };
              } catch (e) {
                console.warn(`Could not check Solana key ${key.key_index}:`, e.message);
                return { isUsed: false, success: false };
              }
            }));
          } catch (e) {
            console.warn('Solana key checking failed, defaulting to available:', e.message);
            checkResults = deposit.keys.map(() => ({ isUsed: false, success: false }));
          }
        } else {
          // No Solana connection - default to available
          checkResults = deposit.keys.map(() => ({ isUsed: false, success: false }));
        }
      } else {
        // EVM keys: Check blockchain for actual key status
        toast.info('Checking key status on blockchain...');

        const { checkMultipleKeys } = await import('../services/treasuryContractService');

        // Prepare key data for checking - include merkle root and denomination
        // IMPORTANT: Use tree_index for nullifier checking, not key_index!
        const keysToCheck = deposit.keys.map(key => ({
          merkleRootId: key.merkle_root_id ?? key.merkleRootId ?? key.batchId ?? key.batch_id,
          merkleRoot: key.merkle_root || key.merkleRoot,
          denomination: key.denomination,
          keyIndex: key.tree_index !== undefined ? key.tree_index : key.treeIndex,  // Use tree_index for nullifier
          treasury_address: key.treasury_address || key.treasuryAddress,
          chain_id: key.chain_id,
          chain_name: key.chain_name,
        }));

        // Group keys by chain_id so we use the correct RPC provider per chain
        const chainGroups = {};
        keysToCheck.forEach((kd, idx) => {
          const cid = kd.chain_id || 'default';
          if (!chainGroups[cid]) chainGroups[cid] = [];
          chainGroups[cid].push({ keyData: kd, originalIndex: idx });
        });

        checkResults = new Array(keysToCheck.length).fill(null);

        for (const [chainId, group] of Object.entries(chainGroups)) {
          // Create a provider for this chain's RPC (avoids wrong-chain nullifier checks)
          let chainProvider = provider;
          if (chainId !== 'default') {
            const rpcUrl = multiTokenTreasuryService.getRpcUrlForChain(parseInt(chainId))
              || multiTokenTreasuryService.getRpcUrlByChainName(group[0].keyData.chain_name);
            if (rpcUrl) {
              try {
                chainProvider = new ethers.providers.JsonRpcProvider(rpcUrl);
              } catch (e) {
                console.warn(`Failed to create provider for chain ${chainId}, falling back to wallet provider:`, e.message);
              }
            }
          }

          const groupKeysToCheck = group.map(g => g.keyData);
          console.log(`Checking ${groupKeysToCheck.length} keys on chain ${chainId}...`);

          try {
            const groupResults = await checkMultipleKeys(chainProvider, groupKeysToCheck);
            group.forEach((g, i) => {
              checkResults[g.originalIndex] = groupResults[i];
            });
          } catch (e) {
            console.warn(`Nullifier check failed for chain ${chainId}:`, e.message);
            group.forEach(g => {
              checkResults[g.originalIndex] = { isUsed: false, success: false, error: e.message };
            });
          }
        }
      }

      // Map keys with blockchain status
      keys = deposit.keys.map((key, index) => {
        const result = checkResults[index];
        return {
          ...key,
          keyIndex: key.key_index,
          key_index: key.key_index,  // Keep original format
          tree_index: key.tree_index,  // IMPORTANT: Include tree_index for merkle proof verification
          merkle_root_id: key.merkle_root_id,  // IMPORTANT: Include merkle_root_id for nullifier checking
          merkleRootId: key.merkle_root_id,  // Also in camelCase
          denomination: key.denomination,
          denomination_wei: key.denomination_wei,  // Include denomination in wei
          merkleRoot: key.merkle_root,  // Map merkle_root to merkleRoot
          merkleProof: key.merkle_proof,
          merkle_root: key.merkle_root,  // Keep original format too
          merkle_proof: key.merkle_proof,
          private_key: key.private_key,  // Ensure private_key is included
          ethereum_address: key.ethereum_address,  // Ensure EVM address is included
          solana_address: key.solana_address,  // Ensure Solana address is included
          chain_name: key.chain_name || 'localhost',  // Include chain name
          chain_id: key.chain_id || 31337,  // Include chain ID
          treasury_address: key.treasury_address,  // Include treasury address for the target chain
          token_symbol: key.token_symbol || 'ETH',  // Include token symbol
          token_address: key.token_address || (key.chain_name?.includes('solana') ? '' : '0x0000000000000000000000000000000000000000'),  // Solana uses base58 mint, EVM uses 0x address
          token_decimals: key.token_decimals,  // Include token decimals if present
          isUsed: result.success ? result.isUsed : false  // Use blockchain status
        };
      });

      // Enhance keys with missing token config from backend (e.g., token_address, token_decimals)
      keys = await Promise.all(keys.map(key => enhanceKeyWithTokenConfig(key)));
    } else if (deposit.keyshares) {
      // Multi-node system: reconstruct keys from keyshares
      keys = await withdrawalService.getAvailableKeys(deposit, provider);
    }

    // Note: The nullifier-based check above (checkMultipleKeys / isKeyUsedOnChain) is the
    // authoritative source for key usage. No need for a redundant WithdrawOTS event scan,
    // which is slow on some RPCs (e.g., Polygon) and causes 30s+ delays.

    // Filter out used keys
    const availableKeys = keys.filter(key => !key.isUsed);

    // If a specific key was requested, filter to only show that key
    let keysToShow = keys;
    if (specificKeyIndex !== null) {
      keysToShow = keys.filter(k => k.keyIndex === specificKeyIndex);
      console.log(`🔑 Filtering to show only key #${specificKeyIndex}:`, keysToShow);

      if (keysToShow.length === 0) {
        toast.error(`Key #${specificKeyIndex} not found`);
        setWithdrawDialog(false);
        return;
      }

      if (keysToShow[0].isUsed) {
        toast.warning(`Key #${specificKeyIndex} has already been used, but you can try again.`);
        // Do not return, allow proceeding
      }
    }

    console.log('🔑 Keys loaded for this deposit:', {
      totalKeys: keys.length,
      keysToShow: keysToShow.length,
      availableKeys: availableKeys.length,
      specificKeyIndex: specificKeyIndex,
      keys: keysToShow.map(k => ({
        keyIndex: k.keyIndex,
        denomination: k.denomination,
        token: k.token_symbol,
        chain: k.chain_name,
        isUsed: k.isUsed
      }))
    });

    if (availableKeys.length === 0) {
      toast.warning('All keys for this deposit have already been used. You can still retry if needed.');
    }

    setAvailableKeys(keysToShow); // Set filtered keys or all keys (including used for retry)
    // Select the specific key or first available key (fall back to first key for retry)
    const keyToSelect = specificKeyIndex !== null ? specificKeyIndex
      : (availableKeys[0] ? availableKeys[0].keyIndex : (keysToShow[0]?.keyIndex ?? null));
    setSelectedKeyIndex(keyToSelect);

    // Reset relay state (default to gas-free relay; manual 'direct' is removed from UI)
    setWithdrawalMode('gasfree');
    setRelayJobId(null);
    setRelayNodeUrl(null);

    setWithdrawDialog(true);
  };

  /**
   * Parse and display withdrawal errors in a user-friendly dialog
   * @param {Error} error - The error object
   * @param {string} chainType - 'solana' or 'evm'
   */
  const handleWithdrawalError = (error, _chainType = 'evm') => {
    console.error('Handling withdrawal error:', error);

    let errorType = 'UNKNOWN_ERROR';
    let title = 'Withdrawal Failed';
    let message = 'An unexpected error occurred during withdrawal.';
    let technicalDetails = error.message || '';
    const msg = error.message || '';

    // 1. Check for contract revert errors first (these contain colons in the message
    //    and would otherwise be mis-parsed by the "ERROR_TYPE:message" format below)
    if (msg.includes('InsufficientGasPool') || msg.includes('relayerGasPool') || (msg.includes('insufficient funds for gas') && msg.includes('relay'))) {
      errorType = 'RELAYER_GAS_ERROR';
      title = 'Relayer Gas Pool Not Funded';
      message = 'The relay service cannot process this withdrawal because the contract\'s relayer gas pool is not funded on this chain. Please try a direct (non-relayed) withdrawal instead, or contact support to fund the gas pool.';
      technicalDetails = msg;
    } else if (msg.includes('InsufficientLiquidity') || msg.includes('transfer amount exceeds balance')) {
      errorType = 'INSUFFICIENT_FUNDS';
      title = 'Insufficient Treasury Liquidity';
      message = 'The treasury contract does not have enough liquidity for this token and amount. The contract needs to be funded before this withdrawal can be processed. Please try again later or contact support.';
      technicalDetails = msg;
    } else if (msg.includes('insufficient funds')) {
      errorType = 'RELAYER_FUNDS';
      title = 'Relayer Wallet Low on Gas';
      message = 'The relay service wallet does not have enough native tokens to cover gas fees on this chain. Please try a direct (non-relayed) withdrawal instead, or contact support.';
      technicalDetails = msg;
    } else if (msg.includes('nullifier already used') || msg.includes('Key already used') || msg.includes('NullifierAlreadyUsed')) {
      errorType = 'KEY_ALREADY_USED';
      title = 'Key Already Used';
      message = 'This withdrawal key has already been used. Each key can only be used once for security reasons. Please select a different key.';
      technicalDetails = msg;
    } else if (msg.includes('MerkleProofInvalid') || msg.includes('invalid proof')) {
      errorType = 'INVALID_PROOF';
      title = 'Invalid Merkle Proof';
      message = 'The merkle proof verification failed on-chain. This key may be invalid or corrupted.';
      technicalDetails = msg;
    } else if (msg.includes('Contract would revert') || msg.includes('execution reverted') || msg.includes('CALL_EXCEPTION')) {
      errorType = 'CONTRACT_ERROR';
      title = 'Smart Contract Error';
      // Try to extract errorName from ethers v5 format: errorName="SomeName"
      const errorNameMatch = msg.match(/errorName="(\w+)"/);
      if (errorNameMatch) {
        message = `The contract reverted with: ${errorNameMatch[1]}. Please check that your key is valid and the contract is properly funded.`;
      } else {
        message = 'The smart contract rejected this transaction. This could be due to an invalid key, insufficient funds, or contract being paused.';
      }
      technicalDetails = msg;
    }
    // 2. Parse formatted errors from services (format: "ERROR_TYPE:User message")
    else if (msg.includes(':')) {
      const [type, ...messageParts] = msg.split(':');
      const trimmedType = type.trim();
      const userMessage = messageParts.join(':').trim();

      switch (trimmedType) {
        case 'KEY_ALREADY_USED':
          errorType = 'KEY_ALREADY_USED';
          title = 'Key Already Used';
          message = userMessage;
          technicalDetails = 'This key has been used in a previous withdrawal transaction. Each key can only be used once to prevent double-spending.';
          break;

        case 'MERKLE_ROOT_INACTIVE':
          errorType = 'MERKLE_ROOT_INACTIVE';
          title = 'Key Batch Inactive';
          message = userMessage;
          technicalDetails = 'The merkle root for this key batch has been deactivated. This usually happens when keys expire or are manually deactivated by administrators.';
          break;

        case 'INVALID_PROOF':
          errorType = 'INVALID_PROOF';
          title = 'Invalid Proof';
          message = userMessage;
          technicalDetails = 'The merkle proof verification failed. This could indicate a corrupted key or database inconsistency.';
          break;

        case 'INSUFFICIENT_FUNDS':
          errorType = 'INSUFFICIENT_FUNDS';
          title = 'Insufficient Treasury Funds';
          message = userMessage;
          technicalDetails = 'The smart contract treasury does not have enough balance to process this withdrawal.';
          break;

        case 'INVALID_SIGNATURE':
        case 'INVALID_PUBLIC_KEY':
          errorType = trimmedType;
          title = 'Invalid Cryptographic Data';
          message = userMessage;
          technicalDetails = 'The cryptographic signature or public key validation failed. This could indicate a corrupted private key.';
          break;

        case 'SYSTEM_PAUSED':
          errorType = 'SYSTEM_PAUSED';
          title = 'System Paused';
          message = userMessage;
          technicalDetails = 'Withdrawals are temporarily paused for maintenance or security reasons.';
          break;

        case 'PROGRAM_ERROR':
        case 'TRANSACTION_FAILED':
          errorType = trimmedType;
          title = 'Transaction Failed';
          message = userMessage;
          break;

        default:
          message = userMessage || msg;
          break;
      }
    }

    // User rejected in wallet — no error dialog needed
    if (error.code === 4001 || error.code === 'ACTION_REJECTED' ||
        (error.message && (error.message.includes('user rejected') || error.message.includes('user denied')))) {
      toast.info('Transaction cancelled by user');
      return;
    }

    // Show the error dialog (no toast - dialog is the primary notification)
    setErrorDetails({
      title,
      message,
      errorType,
      technicalDetails
    });
    setErrorDialog(true);
  };

  // ── Withdrawal sub-functions ──────────────────────────────────

  const executeRelayWithdrawal = async (keyToUse, isSolanaWithdrawal) => {
    if (!relayInfo) {
      throw new Error('Relayer information not available. Please try again.');
    }

    const keyChainType = getChainType(keyToUse?.ethereum_address || keyToUse?.solana_address);
    const isTronKey = keyChainType === 'tron' ||
      keyToUse?.chain_name?.toLowerCase().startsWith('tron') ||
      keyToUse?.chain_type === 'tron';

    // Check relayer on-chain balance before attempting relay (EVM/Solana only;
    // Tron balance precheck would require a separate TronGrid call).
    if (isTronKey) {
      // No precheck — dkgSimpleWithdrawal handles Tron routing via tron_relayer_address.
    } else if (isSolanaWithdrawal) {
      if (relayInfo.solana_relayer_address && solanaConnection) {
        try {
          const { PublicKey } = await import('@solana/web3.js');
          const relayerPubkey = new PublicKey(relayInfo.solana_relayer_address);
          const balance = await solanaConnection.getBalance(relayerPubkey);
          if (balance === 0) {
            throw new Error(
              'The relayer wallet has no SOL balance and cannot pay gas for your withdrawal. Please use Direct Withdrawal instead.'
            );
          }
        } catch (e) {
          if (e.message.includes('Direct Withdrawal')) throw e;
          console.warn('Failed to check Solana relayer balance:', e.message);
        }
      }
    } else {
      if (relayInfo.evm_relayer_address && provider) {
        try {
          const balance = await provider.getBalance(relayInfo.evm_relayer_address);
          if (balance.isZero()) {
            throw new Error(
              'The relayer wallet has no balance and cannot pay gas for your withdrawal. Please use Direct Withdrawal instead.'
            );
          }
        } catch (e) {
          if (e.message.includes('Direct Withdrawal')) throw e;
          console.warn('Failed to check EVM relayer balance:', e.message);
        }
      }
    }

    const onProgress = (label) => (progress) => {
      console.log(`${label} progress:`, progress);
      if (progress.jobId) {
        setRelayJobId(progress.jobId);
        setRelayNodeUrl(relayInfo.nodeUrl);
      }
    };

    let relayResult;

    if (isSolanaWithdrawal) {
      // Solana relay path
      // maxRelayerFee is a user-signed cap — contract charges actual fees (protocol_fee + fixed_relayer_fee).
      // Use generous cap: 10% of amount or minimum 0.01 SOL (10M lamports), whichever is higher.
      const denomBaseUnits = keyToUse.denomination_wei
        ? BigInt(keyToUse.denomination_wei)
        : BigInt(Math.floor(parseFloat(keyToUse.denomination) * 1e9));
      const tenPercent = denomBaseUnits / 10n;
      const minCap = 10_000_000n; // 0.01 SOL
      const maxFee = (tenPercent > minCap ? tenPercent : minCap).toString();

      relayResult = await executeSolanaRelayedWithdrawal({
        key: keyToUse,
        recipientAddress,
        relayInfo,
        maxRelayerFee: maxFee,
        nodeUrl: relayInfo.nodeUrl,
        isSwap: withdrawalMode === 'swap',
        targetAddress: null,
      }, onProgress('Solana relay'));
    } else {
      // EVM / Tron relay path (dkgSimpleWithdrawal auto-detects Tron from key)
      const isNative = !keyToUse.token_address || keyToUse.token_address === '0x0000000000000000000000000000000000000000';
      const maxFee = estimateMaxRelayerFee(relayInfo, keyToUse.denomination_wei, isNative);

      // EVM-only: check realistic fees vs denomination (uses EVM provider).
      if (isNative && !isTronKey) {
        let currentGasPrice = null;
        try {
          if (provider) currentGasPrice = await provider.getGasPrice();
        } catch (e) {
          console.warn('Failed to fetch gas price for relay estimate:', e);
        }
        const denomBN = ethers.BigNumber.from(keyToUse.denomination_wei);
        const realisticFee = ethers.BigNumber.from(estimateRealisticRelayFee(relayInfo, keyToUse.denomination_wei, true, currentGasPrice));
        if (realisticFee.gte(denomBN)) {
          throw new Error(
            `Denomination too small for gas-free relay. The estimated relay fee (~${parseFloat(ethers.utils.formatEther(realisticFee)).toFixed(6)} ETH) exceeds the key value (${keyToUse.denomination} ETH). Please use Direct Withdrawal instead.`
          );
        }
      }

      relayResult = await executeRelayedWithdrawal({
        key: keyToUse,
        recipientAddress,
        relayInfo,
        maxRelayerFee: maxFee,
        nodeUrl: relayInfo.nodeUrl,
        isSwap: withdrawalMode === 'swap',
        targetAddress: null,
      }, onProgress('Relay'));
    }

    if (relayResult.success) {
      return { success: true, txHash: relayResult.txHash, receipt: { transactionHash: relayResult.txHash } };
    }
    throw new Error(relayResult.error || 'Relayed withdrawal failed');
  };

  const executeDirectSolanaWithdrawal = async () => {
    const keyToUse = withdrawDeposit.keys.find(k => k.key_index === selectedKeyIndex);
    if (!keyToUse) throw new Error('Selected Solana key not found');

    console.log('🟣 Executing Solana withdrawal...', {
      keyIndex: keyToUse.key_index,
      solanaAddress: keyToUse.solana_address || keyToUse.ethereum_address,
      denomination: keyToUse.denomination,
      tokenSymbol: keyToUse.token_symbol,
      chainName: keyToUse.chain_name,
      hasPrivateKey: !!keyToUse.private_key,
      keyFields: Object.keys(keyToUse),
    });

    // Try backend RPC for better performance, fall back to default connection
    let connectionToUse = solanaConnection;
    const backendRpcUrl = multiTokenTreasuryService.getRpcUrlByChainName(keyToUse.chain_name || 'solana-devnet');
    if (backendRpcUrl) {
      console.log('🔗 Using backend RPC for Solana:', backendRpcUrl);
      connectionToUse = new Connection(backendRpcUrl, 'confirmed');
    }

    const result = await solanaInternullService.executeWithdrawal(keyToUse, recipientAddress, connectionToUse, solanaWallet);
    console.log('Solana withdrawal result:', result);
    if (!result.success) throw new Error(result.error || 'Solana withdrawal failed');
    return result;
  };

  const executeDirectEvmWithdrawal = async (selectedKey) => {
    if (withdrawDeposit.keys) {
      // Single-node system
      const keyToUse = withdrawDeposit.keys.find(k => k.key_index === selectedKeyIndex);
      if (!keyToUse) throw new Error('Selected key not found');

      const keyChainId = keyToUse.chain_id;
      const keyChainConfig = supportedChains.find(c => c.chain_id === keyChainId);
      if (!keyChainConfig) {
        throw new Error(`Chain configuration not found for ${keyToUse.chain_name} (ID: ${keyChainId})`);
      }

      console.log('Key requires chain:', keyToUse.chain_name, 'Chain ID:', keyChainId);

      const metamaskProvider = getMetaMaskProvider();
      if (!metamaskProvider) {
        throw new Error('MetaMask not found. Please install MetaMask for EVM withdrawals.');
      }

      // Check current network and switch if needed
      const currentChainIdHex = await metamaskProvider.request({ method: 'eth_chainId' });
      const currentChainId = parseInt(currentChainIdHex, 16);
      console.log('Current MetaMask chain ID:', currentChainId);

      if (currentChainId !== keyChainId) {
        console.log(`Switching MetaMask to ${keyToUse.chain_name} (chain ID: ${keyChainId})`);
        toast.info(`Please switch MetaMask to ${keyChainConfig.name} to continue withdrawal`);
        try {
          await metamaskProvider.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: `0x${keyChainId.toString(16)}` }],
          });
          await new Promise(resolve => setTimeout(resolve, 2000));
          toast.success(`Switched to ${keyChainConfig.name}`);
        } catch (switchError) {
          console.error('Network switch error:', switchError);
          if (switchError.code === 4902) {
            // Auto-add the chain to the wallet
            const CHAIN_PARAMS = {
              11155111: { chainName: 'Sepolia Testnet', nativeCurrency: { name: 'SepoliaETH', symbol: 'ETH', decimals: 18 }, rpcUrls: [process.env.REACT_APP_RPC_URL || 'https://eth-sepolia.public.blastapi.io'], blockExplorerUrls: ['https://sepolia.etherscan.io'] },
              84532: { chainName: 'Base Sepolia', nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 }, rpcUrls: ['https://sepolia.base.org'], blockExplorerUrls: ['https://sepolia.basescan.org'] },
              80002: { chainName: 'Polygon Amoy Testnet', nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 }, rpcUrls: ['https://rpc-amoy.polygon.technology'], blockExplorerUrls: ['https://amoy.polygonscan.com'] },
              97: { chainName: 'BNB Smart Chain Testnet', nativeCurrency: { name: 'Test BNB', symbol: 'tBNB', decimals: 18 }, rpcUrls: ['https://data-seed-prebsc-1-s1.bnbchain.org:8545'], blockExplorerUrls: ['https://testnet.bscscan.com'] },
              998: { chainName: 'Hyperliquid Testnet', nativeCurrency: { name: 'USD', symbol: 'USD', decimals: 6 }, rpcUrls: ['https://api.hyperliquid-testnet.xyz/evm'], blockExplorerUrls: ['https://explorer.hyperliquid-testnet.xyz'] },
            };
            const params = CHAIN_PARAMS[keyChainId];
            if (params) {
              toast.info(`Adding ${params.chainName} to your wallet...`);
              await metamaskProvider.request({
                method: 'wallet_addEthereumChain',
                params: [{ chainId: `0x${keyChainId.toString(16)}`, ...params }],
              });
              await new Promise(resolve => setTimeout(resolve, 1000));
              toast.success(`Added and switched to ${params.chainName}`);
            } else {
              toast.error(`Please add chain ${keyChainId} to MetaMask manually`);
              throw new Error(`Chain ${keyChainConfig.name} not configured in MetaMask`);
            }
          } else {
            throw switchError;
          }
        }
      }

      // Create fresh provider from MetaMask
      const withdrawalProvider = new ethers.providers.Web3Provider(metamaskProvider);
      const finalChainId = (await withdrawalProvider.getNetwork()).chainId;
      console.log(`✅ Provider connected to network ${finalChainId}`);

      if (finalChainId !== keyChainId) {
        throw new Error(`Network mismatch! Expected ${keyChainId}, but provider is on ${finalChainId}`);
      }

      console.log('Initializing singleNodeWithdrawal for:', keyToUse.chain_name, 'Treasury:', keyToUse.treasury_address);
      await singleNodeWithdrawal.initialize(
        withdrawalProvider.provider || withdrawalProvider,
        keyToUse.treasury_address
      );

      return await singleNodeWithdrawal.executeWithdrawalWithKey(keyToUse, recipientAddress);
    }

    // Multi-node system: use ECDSA service
    const treasuryAddress = selectedKey.treasury_address || selectedKey.treasuryAddress;
    console.log('Using treasury address for multi-node withdrawal:', treasuryAddress);

    return await metaMaskOptimizedServiceECDSA.tryDifferentApproaches(
      provider, signer,
      { recipient: recipientAddress, denomination: selectedKey.denomination, keyshares: withdrawDeposit.keyshares, keyIndex: selectedKeyIndex },
      treasuryAddress
    );
  };

  const handleWithdrawalSuccess = (result, selectedKey) => {
    const withdrawalTxHash = result.receipt?.transactionHash || result.txHash;

    // Mark the key as used in the FULL deposit keys array
    const fullDepositKeys = withdrawDeposit.keys || [];
    const markUsed = (k) =>
      (k.keyIndex === selectedKeyIndex || k.key_index === selectedKeyIndex)
        ? { ...k, isUsed: true, withdrawalTxHash, withdrawalTimestamp: Date.now(), withdrawalRecipient: recipientAddress }
        : k;

    const updatedKeys = fullDepositKeys.map(markUsed);
    setAvailableKeys(availableKeys.map(markUsed));

    // Determine new deposit status
    const hasUnusedKeys = updatedKeys.some(k => !k.isUsed);
    const totalKeyValueAfterWithdraw = updatedKeys.reduce((sum, k) => sum + parseFloat(k.denomination || 0), 0);
    const depositAmount = withdrawDeposit.depositAmount || withdrawDeposit.amount || 0;
    const hasUnclaimedBalance = depositAmount > 0 && totalKeyValueAfterWithdraw < depositAmount - 0.000001;
    const newStatus = hasUnclaimedBalance ? 'confirmed' : (hasUnusedKeys ? 'keys_received' : 'withdrawn');

    // Build withdrawal record
    const existingWithdrawals = withdrawDeposit.withdrawals || [];
    const recordingKey = fullDepositKeys.find(k =>
      k.keyIndex === selectedKeyIndex || k.key_index === selectedKeyIndex
    ) || selectedKey;
    const newWithdrawal = {
      txHash: withdrawalTxHash, timestamp: Date.now(), recipient: recipientAddress, keyIndex: selectedKeyIndex,
      chainName: recordingKey?.chain_name || 'Unknown',
      tokenSymbol: recordingKey?.token_symbol || recordingKey?.tokenSymbol || 'ETH',
      amount: recordingKey?.denomination || '0',
      chainType: recordingKey?.chain_type || 'evm',
    };

    // Ensure deposit exists in tracker. Use the chain-appropriate user address:
    // Tron deposits are saved under T... base58, Solana under base58, EVM under
    // lowercased 0x...; falling back to `account` for a Tron deposit would
    // store the withdrawal against the wrong address and hide it from History.
    const recordingChainType = (recordingKey?.chain_name?.toLowerCase().startsWith('tron') || recordingKey?.chain_type === 'tron') ? 'tron'
      : (recordingKey?.chain_name?.toLowerCase().includes('solana') || recordingKey?.solana_address) ? 'solana'
      : 'evm';
    const withdrawUserAddr = recordingChainType === 'tron'
      ? (tronAddress || withdrawDeposit.userAddress || '')
      : recordingChainType === 'solana'
        ? (solanaPublicKey?.toString() || withdrawDeposit.userAddress || '')
        : (account || withdrawDeposit.userAddress || '');
    depositTracker.ensureDeposit(
      withdrawDeposit.txHash, withdrawUserAddr, withdrawDeposit.amount || 0,
      recordingKey?.chain_id || null,
      { token: recordingKey?.token_symbol || 'ETH', tokenAddress: recordingKey?.token_address || '' }
    );

    // Update deposit tracker
    const updateData = { withdrawals: [...existingWithdrawals, newWithdrawal], keys: updatedKeys };
    if (newStatus === 'confirmed' && hasUnclaimedBalance) {
      updateData.remainingDeposit = depositAmount - totalKeyValueAfterWithdraw;
      updateData.claimedAmount = totalKeyValueAfterWithdraw;
      updateData.depositAmount = depositAmount;
    }
    depositTracker.updateDepositStatus(withdrawDeposit.txHash, newStatus, updateData);

    // Update React state for instant UI feedback
    setDeposits(prevDeposits => {
      const exists = prevDeposits.some(d => d.txHash === withdrawDeposit.txHash);
      if (exists) {
        return prevDeposits.map(d =>
          d.txHash === withdrawDeposit.txHash
            ? { ...d, status: newStatus, keys: updatedKeys, withdrawals: [...existingWithdrawals, newWithdrawal] }
            : d
        );
      }
      // Preserve original case for Tron/Solana base58 addresses; only EVM is
      // case-insensitive and should be lowercased.
      const normalisedUserAddr = (recordingChainType === 'evm' && withdrawUserAddr)
        ? withdrawUserAddr.toLowerCase()
        : withdrawUserAddr;
      return [{
        ...withdrawDeposit, status: newStatus, keys: updatedKeys,
        withdrawals: [...existingWithdrawals, newWithdrawal], userAddress: normalisedUserAddr,
      }, ...prevDeposits];
    });

    setWithdrawDialog(false);
    loadUserDeposits();

    // Show success dialog
    const chainName = selectedKey?.chain_name || 'sepolia';
    setSuccessDetails({
      title: 'Withdrawal Successful',
      message: `Successfully withdrew ${selectedKey?.denomination || ''} ${selectedKey?.token_symbol || 'ETH'} to ${recipientAddress.slice(0, 6)}...${recipientAddress.slice(-4)}`,
      txHash: withdrawalTxHash,
      explorerUrl: getExplorerUrl(chainName, withdrawalTxHash),
      chainName: getChainDisplayName(chainName),
    });
    setSuccessDialog(true);
  };

  // ── Main withdrawal orchestrator ────────────────────────────────

  const executeWithdrawal = async () => {
    if (!withdrawDeposit) {
      toast.error('Missing required data for withdrawal');
      return;
    }

    const selectedKey = availableKeys.find(key => key.keyIndex === selectedKeyIndex);
    const isSolanaWithdrawal = selectedKey && (
      selectedKey.chain_name?.toLowerCase().includes('solana') ||
      selectedKey.solana_address ||
      selectedKey.chain_type === 'solana'
    );
    const isTronWithdrawal = selectedKey && (
      selectedKey.chain_name?.toLowerCase().startsWith('tron') ||
      selectedKey.chain_type === 'tron'
    );
    const isGasFree = withdrawalMode === 'gasfree' || withdrawalMode === 'swap';

    // Validate wallet connection and address format
    if (isSolanaWithdrawal) {
      if (!isGasFree && (!solanaPublicKey || !solanaConnected || !solanaConnection)) {
        toast.error('Please connect your Phantom wallet for direct Solana withdrawals');
        return;
      }
      if (getChainType(recipientAddress) !== 'solana') {
        toast.error('Please enter a valid Solana address');
        return;
      }
      try {
        const { PublicKey } = await import('@solana/web3.js');
        new PublicKey(recipientAddress);
      } catch (e) {
        toast.error('Invalid Solana address format');
        return;
      }
    } else if (isTronWithdrawal) {
      // Tron only supports relayed withdrawal in v1 (TronLink can't pay gas for
      // the contract-signed flow we use). Force gas-free if not already.
      if (!isGasFree) {
        toast.info('Tron withdrawals are routed through the relayer.');
      }
      if (getChainType(recipientAddress) !== 'tron') {
        toast.error('Please enter a valid Tron address');
        return;
      }
    } else {
      if (!isGasFree && (!provider || !signer)) {
        toast.error('Please connect your MetaMask wallet for direct withdrawals');
        return;
      }
      if (!recipientAddress || !ethers.utils.isAddress(recipientAddress)) {
        toast.error('Please enter a valid EVM address');
        return;
      }
    }

    if (selectedKeyIndex === null) { toast.error('Please select a key to withdraw'); return; }
    if (!selectedKey) { toast.error('Selected key not found'); return; }
    if (selectedKey.isUsed) {
      toast.warning('Selected key has already been used. Proceeding anyway as requested.');
    }

    try {
      setWithdrawing(true);
      let result;

      if (isGasFree || isTronWithdrawal) {
        const keyToUse = withdrawDeposit.keys.find(k => k.key_index === selectedKeyIndex);
        if (!keyToUse) throw new Error('Selected key not found');
        result = await executeRelayWithdrawal(keyToUse, isSolanaWithdrawal);
      } else if (isSolanaWithdrawal) {
        result = await executeDirectSolanaWithdrawal();
      } else {
        result = await executeDirectEvmWithdrawal(selectedKey);
      }

      if (result.success) {
        handleWithdrawalSuccess(result, selectedKey);
      } else {
        throw new Error(result.error || 'Withdrawal failed. Please try again.');
      }
    } catch (error) {
      console.error('Withdrawal failed:', error);
      const chainType = selectedKey?.chain_name === 'solana-devnet' || selectedKey?.chain_name === 'solana' ? 'solana' : 'evm';
      handleWithdrawalError(error, chainType);
    } finally {
      setWithdrawing(false);
      setRelayJobId(null);
      setRelayNodeUrl(null);
    }
  };

  const executeKeyRequest = async () => {
    console.log('🚀 executeKeyRequest called');
    console.log('📋 selectedDeposit:', selectedDeposit);

    if (!selectedDeposit) {
      console.error('❌ Missing selectedDeposit');
      toast.error('Please select a deposit first');
      return;
    }

    // Determine if this is a Solana or EVM deposit
    const isSolanaDeposit = getChainType(selectedDeposit.userAddress) === 'solana';
    console.log('🔍 Deposit type:', isSolanaDeposit ? 'Solana' : 'EVM');

    try {
      setKeyRequestLoading(true);
      console.log('✅ Starting key request process...');

      // Parse and validate denomination requests
      const validRequests = denominationRequests
        .filter(req => req.denomination && parseFloat(req.denomination) > 0)
        .map(req => ({
          denomination: parseFloat(req.denomination),
          chain: req.chain,
          token: req.token
        }));

      if (validRequests.length === 0) {
        toast.error('Please enter at least one valid denomination');
        return;
      }

      const totalRequested = validRequests.reduce((sum, req) => sum + req.denomination, 0);
      if (totalRequested > selectedDeposit.amount) {
        toast.error('Total requested denominations cannot exceed deposit amount');
        return;
      }

      // Convert denomination to string for database lookup
      const formatDenomination = (d) => {
        // Return as string without adding decimal points
        // Database stores whole numbers as "1", "2", "5", not "1.0", "2.0", "5.0"
        return d.toString();
      };

      if (isSolanaDeposit) {
        // ===== SOLANA FLOW =====
        console.log('🟣 Processing Solana key request...');

        // Check Solana wallet connection
        if (!solanaPublicKey || !solanaConnected) {
          toast.error('Please connect your Phantom wallet first');
          return;
        }

        // Verify wallet matches deposit
        const currentSolanaAddress = solanaPublicKey.toString();
        // Case-insensitive comparison for Solana addresses (in case deposit was stored with different casing)
        if (currentSolanaAddress.toLowerCase() !== selectedDeposit.userAddress.toLowerCase()) {
          toast.error(`❌ Wallet mismatch! This deposit was made with ${selectedDeposit.userAddress} but you're connected with ${currentSolanaAddress}. Please switch to the correct wallet.`);
          return;
        }

        // Build withdrawal requests for Solana (matching EVM format)
        const withdrawalRequests = validRequests.map(req => ({
          target_chain: req.chain,
          token_symbol: req.token,
          denomination: formatDenomination(req.denomination)
        }));

        console.log('🪙 Requesting Solana keys with withdrawal requests:', withdrawalRequests);

        // Generate timestamp for signature
        const timestamp = Math.floor(Date.now() / 1000);

        // Create the JSON message to sign (MUST match EVM format for backend compatibility)
        // Note: Solana addresses are case-sensitive base58 strings, do NOT lowercase them
        const message = {
          action: 'request_withdrawal',
          user_address: currentSolanaAddress,  // Keep original case for Solana addresses
          deposit_tx: selectedDeposit.txHash,
          source_chain: sourceChain,
          withdrawal_requests: withdrawalRequests,
          timestamp: timestamp
        };

        console.log('🔐 Message to sign:', JSON.stringify(message, null, 2));

        // Helper function to recursively sort object keys (matching Python's sort_keys=True)
        const sortKeysRecursive = (obj) => {
          if (Array.isArray(obj)) {
            return obj.map(item => sortKeysRecursive(item));
          } else if (obj !== null && typeof obj === 'object') {
            const sortedKeys = Object.keys(obj).sort();
            const sortedObj = {};
            sortedKeys.forEach(key => {
              sortedObj[key] = sortKeysRecursive(obj[key]);
            });
            return sortedObj;
          }
          return obj;
        };

        // Sort keys recursively to match backend format: json.dumps(message_data, sort_keys=True)
        const sortedMessage = sortKeysRecursive(message);

        // Python's json.dumps() uses separators=(', ', ': ') by default (with spaces)
        // JavaScript's JSON.stringify() uses separators=(',', ':') by default (no spaces)
        // We need to match Python's format by adding spaces after colons and commas
        const compactStr = JSON.stringify(sortedMessage);
        const messageStr = compactStr.replace(/,/g, ', ').replace(/:/g, ': ');

        console.log('📄 Formatted message string to sign:', messageStr);

        // Sign message with Phantom wallet
        // Note: Solana uses ed25519 signatures
        const encodedMessage = new TextEncoder().encode(messageStr);

        let signature;
        try {
          // @ts-ignore - Phantom wallet has signMessage method
          const signedMessage = await window.solana.signMessage(encodedMessage, 'utf8');
          signature = Buffer.from(signedMessage.signature).toString('hex');
          console.log('📝 Message signed with Phantom wallet');
        } catch (signError) {
          console.error('Failed to sign message:', signError);
          toast.error('Failed to sign message with Phantom wallet. Please try again.');
          return;
        }

        // Call backend endpoint with structured withdrawal requests
        const result = await solanaInternullService.requestCrossChainKeys(
          selectedDeposit.txHash,
          sourceChain,
          withdrawalRequests,  // Pass the full withdrawal_requests array
          currentSolanaAddress,
          '0x' + signature,
          timestamp
        );

        console.log('✅ Result from Solana backend:', result);

        if (result.success) {
          // Store keys in deposit tracker
          depositTracker.updateDepositStatus(selectedDeposit.txHash, 'keys_received', {
            requestId: result.request_id,
            keys: result.keys,
            total_value: result.total_value,
            keysReceived: true
          });

          toast.success(`✅ Keys received successfully! Got ${result.keys?.length || 0} key(s) totaling ${result.total_value} SOL`);

          setKeyRequestDialog(false);
          loadUserDeposits(); // Refresh
        } else {
          throw new Error(result.error || 'Failed to request keys from backend');
        }

      } else {
        // ===== EVM FLOW =====
        console.log('🔵 Processing EVM key request...');

        // Check EVM wallet connection
        if (!signer) {
          toast.error('Please connect your MetaMask wallet first');
          return;
        }

        // Verify wallet matches deposit
        const currentAddress = await signer.getAddress();
        if (currentAddress.toLowerCase() !== selectedDeposit.userAddress.toLowerCase()) {
          toast.error(`❌ Wallet mismatch! This deposit was made with address ${selectedDeposit.userAddress} but you're connected with ${currentAddress}. Please switch to the correct wallet.`);
          return;
        }

        // Ensure service is initialized before using it
        if (!singleNodeWithdrawal.isInitialized && provider) {
          console.log('Initializing singleNodeWithdrawal before use...');
          await singleNodeWithdrawal.initialize(provider.provider || provider);
        }

        // Build withdrawal_requests array for backend with per-request token selection
        const withdrawalRequests = validRequests.map(req => ({
          target_chain: req.chain,
          token_symbol: req.token,  // Use per-request token selection
          denomination: formatDenomination(req.denomination)
        }));

        console.log('🪙 Requesting EVM keys with mixed chains:', withdrawalRequests);
        console.log('📝 About to call requestKeysOnlyMixed...');
        console.log('   userAddress:', selectedDeposit.userAddress);
        console.log('   depositTx:', selectedDeposit.txHash);
        console.log('   sourceChain:', sourceChain);

        const metamaskProvider = getMetaMaskProvider();
        if (!metamaskProvider) {
          throw new Error('MetaMask provider not found');
        }

        const result = await singleNodeWithdrawal.requestKeysOnlyMixed({
          userAddress: selectedDeposit.userAddress,
          depositTx: selectedDeposit.txHash,
          sourceChain,  // Include source chain (where deposit was made)
          withdrawalRequests,  // Array of requests with different chains
          web3Provider: metamaskProvider,  // Pass MetaMask provider specifically
          pausePollingWrapper: executeWithPausedPolling  // Pass wrapper to pause polling during signature
        });
        console.log('✅ Result from requestKeysOnlyMixed:', result);

        if (result.success) {
          // Store keys in deposit tracker for later withdrawal
          depositTracker.updateDepositStatus(selectedDeposit.txHash, 'keys_received', {
            requestId: result.request_id,
            keys: result.keys,
            total_value: result.total_value,
            keysReceived: true
          });

          // Get unique tokens from withdrawal requests for the success message
          const uniqueTokens = [...new Set(withdrawalRequests.map(r => r.token_symbol))];
          const tokensStr = uniqueTokens.join('/');

          toast.success(`✅ Keys received successfully! Got ${result.keys.length} key(s) totaling ${result.total_value} ${tokensStr}`);

          setKeyRequestDialog(false);
          loadUserDeposits(); // Refresh
        }
      }

    } catch (error) {
      console.error('❌ Error requesting keys:', error);
      console.error('❌ Error name:', error.name);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error stack:', error.stack);
      console.error('❌ Error code:', error.code);
      toast.error(`Failed to request keys: ${error.message}`);
    } finally {
      console.log('🏁 Key request process finished, setting loading to false');
      setKeyRequestLoading(false);
    }
  };


  const [downloadKeysData, setDownloadKeysData] = useState(null);

  const handleDownloadKeys = (keyIndex = null, depositForDownload = null) => {
    setDownloadKeyIndex(keyIndex);
    // Store the keys from the deposit if provided
    if (depositForDownload?.keys) {
      console.log('📦 Storing keys for download:', depositForDownload.keys.length, 'keys');
      // Normalize keys to ensure keyIndex field exists (might be key_index in raw deposit)
      const normalizedKeys = depositForDownload.keys.map(key => ({
        ...key,
        keyIndex: key.keyIndex || key.key_index,
        key_index: key.key_index || key.keyIndex,
      }));
      console.log('🔑 Normalized keyIndexes:', normalizedKeys.map(k => k.keyIndex));
      setDownloadKeysData({ keys: normalizedKeys, deposit: depositForDownload });
    } else {
      setDownloadKeysData(null);
    }
    setPasswordDialogMode('download');
    setPasswordDialog(true);
  };

  const handleUploadKeys = () => {
    fileInputRef.current?.click();
  };

  const downloadKeysWithPassword = async (password) => {
    try {
      // Get keys from the current state - try multiple sources
      let keysForDownload = availableKeys;
      let depositForHelper = withdrawDeposit;

      // If availableKeys is empty, try downloadKeysData (from card view)
      if ((!keysForDownload || keysForDownload.length === 0) && downloadKeysData?.keys) {
        console.log('📝 Using keys from downloadKeysData');
        keysForDownload = downloadKeysData.keys;
        depositForHelper = downloadKeysData.deposit;
      }
      // Otherwise try withdrawDeposit.keys (from withdraw dialog)
      else if ((!keysForDownload || keysForDownload.length === 0) && withdrawDeposit?.keys) {
        console.log('📝 Using keys from withdrawDeposit');
        keysForDownload = withdrawDeposit.keys;
      }

      if (!keysForDownload || keysForDownload.length === 0) {
        throw new Error('No keys available to download');
      }

      console.log('🔑 Keys for download:', keysForDownload.length, 'keys');
      console.log('🔍 Looking for keyIndex:', downloadKeyIndex);
      console.log('🔍 Available keyIndexes:', keysForDownload.map(k => k.keyIndex || k.key_index));

      // Use the helper to prepare keys for download (handles single node mode)
      const { keysData, filename } = prepareKeysForDownload(
        keysForDownload,
        depositForHelper,
        downloadKeyIndex
      );

      // Encrypt and download
      const encryptedData = KeyEncryptionService.encryptKeys(keysData, password);
      KeyEncryptionService.downloadEncryptedKeys(encryptedData, filename);

      const message = downloadKeyIndex !== null
        ? `Key ${downloadKeyIndex} downloaded successfully! Keep your password safe.`
        : 'All keys downloaded successfully! Keep your password safe.';
      toast.success(message);

      // Reset download key index and data
      setDownloadKeyIndex(null);
      setDownloadKeysData(null);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download keys: ' + error.message);
      throw error;
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const encryptedData = await KeyEncryptionService.readEncryptedFile(file);
      setPasswordDialogMode('upload');
      setPasswordDialog(true);

      // Store the encrypted data temporarily
      encryptedDataRef.current = encryptedData;
    } catch (error) {
      console.error('File read error:', error);
      toast.error('Failed to read file: ' + error.message);
    }

    // Reset file input
    event.target.value = '';
  };

  // ── Key upload sub-functions ──────────────────────────────────

  // Enrich keys with metadata from uploaded file
  const enrichKeysWithMetadata = (keysWithStatus, uploadedKeys) => {
    return keysWithStatus.map(key => {
      const uploadedKey = uploadedKeys?.find(k =>
        k.keyIndex === key.keyIndex ||
        k.keyIndex === String(key.keyIndex) ||
        k.keyIndex === Number(key.keyIndex)
      );
      if (!uploadedKey) return key;
      return {
        ...key,
        userAddress: uploadedKey.userAddress || key.userAddress,
        amount: uploadedKey.amount || key.amount || key.denomination,
        merkleRoot: uploadedKey.merkleRoot || key.merkleRoot,
        merkleProof: uploadedKey.merkleProof || key.merkleProof,
        treeIndex: uploadedKey.treeIndex || key.treeIndex,
        batchId: uploadedKey.batchId || key.batchId,
        originalMetadata: uploadedKey.originalMetadata || {}
      };
    });
  };

  // Process single-node key upload with blockchain verification
  // Returns false if keys weren't loaded (all used or user cancelled)
  const processSingleNodeUpload = async (keysData) => {
    console.log(`Processing ${keysData.availableKeys.length} keys from file`);

    const loadingToast = toast.info(
      `🔍 Verifying ${keysData.availableKeys.length} key${keysData.availableKeys.length > 1 ? 's' : ''} against blockchain...`,
      { autoClose: false }
    );

    let verifiedKeys = [];

    try {
      // Separate Solana keys from EVM keys
      const solanaKeyIndices = [];
      const evmKeys = [];
      const evmKeyIndices = [];

      keysData.availableKeys.forEach((key, index) => {
        const isSolanaKey = key.chain_name?.startsWith('solana') ||
          key.chainName?.startsWith('solana') ||
          key.solana_address !== undefined;
        if (isSolanaKey) {
          solanaKeyIndices.push(index);
        } else {
          evmKeys.push(key);
          evmKeyIndices.push(index);
        }
      });

      console.log(`Detected ${solanaKeyIndices.length} Solana keys and ${evmKeys.length} EVM keys`);

      const checkResults = new Array(keysData.availableKeys.length);

      // Solana keys: mark as verified without on-chain check
      solanaKeyIndices.forEach(index => {
        checkResults[index] = { success: true, isUsed: false, note: 'Solana key - on-chain verification skipped' };
      });

      // EVM keys: check against contract (grouped by chain for correct RPC)
      if (evmKeys.length > 0) {
        const { checkMultipleKeys } = await import('../services/treasuryContractService');
        const keysToCheck = evmKeys.map(key => ({
          merkleRootId: key.batchId || key.batch_id || key.merkleRootId,
          merkleRoot: key.merkleRoot || key.merkle_root,
          denomination: key.denomination,
          keyIndex: key.treeIndex || key.tree_index || key.keyIndex || key.key_index,
          treasury_address: key.treasury_address || key.treasuryAddress,
          chain_id: key.chain_id || key.chainId,
          chain_name: key.chain_name || key.chainName,
        }));

        // Group by chain_id to use correct RPC provider
        const chainGroups = {};
        keysToCheck.forEach((kd, idx) => {
          const cid = kd.chain_id || 'default';
          if (!chainGroups[cid]) chainGroups[cid] = [];
          chainGroups[cid].push({ keyData: kd, groupIndex: idx });
        });

        const evmCheckResults = new Array(keysToCheck.length).fill(null);
        for (const [chainId, group] of Object.entries(chainGroups)) {
          let chainProvider = provider;
          if (chainId !== 'default') {
            const rpcUrl = multiTokenTreasuryService.getRpcUrlForChain(parseInt(chainId))
              || multiTokenTreasuryService.getRpcUrlByChainName(group[0].keyData.chain_name);
            if (rpcUrl) {
              try { chainProvider = new ethers.providers.JsonRpcProvider(rpcUrl); } catch (e) { /* fallback */ }
            }
          }
          try {
            const groupResults = await checkMultipleKeys(chainProvider, group.map(g => g.keyData));
            group.forEach((g, i) => { evmCheckResults[g.groupIndex] = groupResults[i]; });
          } catch (e) {
            group.forEach(g => { evmCheckResults[g.groupIndex] = { isUsed: false, success: false, error: e.message }; });
          }
        }

        evmKeyIndices.forEach((originalIndex, i) => {
          checkResults[originalIndex] = evmCheckResults[i];
        });
      }

      // Process results
      let usedKeysCount = 0;
      let errorKeysCount = 0;

      keysData.availableKeys.forEach((key, index) => {
        const result = checkResults[index];
        if (result.success) {
          if (!result.isUsed) {
            verifiedKeys.push({ ...key, isUsed: false });
          } else {
            usedKeysCount++;
            console.log(`Key ${key.keyIndex} at address ${key.ethereum_address} has already been used on blockchain`);
          }
        } else {
          errorKeysCount++;
          if (result.error && !result.error.includes('merkleRootId')) {
            console.warn(`Could not verify key: ${result.error}`);
          }
          verifiedKeys.push({ ...key, isUsed: false, verificationFailed: true });
        }
      });

      toast.dismiss(loadingToast);

      if (errorKeysCount > 0) {
        toast.warning(`⚠️ ${errorKeysCount} key${errorKeysCount > 1 ? 's' : ''} could not be verified. They have been included but use with caution.`, { autoClose: 5000 });
      }
      if (usedKeysCount > 0) {
        toast.warning(`⚠️ ${usedKeysCount} key${usedKeysCount > 1 ? 's have' : ' has'} already been used and ${usedKeysCount > 1 ? 'were' : 'was'} filtered out`, { autoClose: 5000 });
      }
      if (verifiedKeys.length === 0) {
        toast.error('❌ All keys have already been used on the blockchain. Please load a different key file.');
        return false;
      }

    } catch (verifyError) {
      toast.dismiss(loadingToast);
      console.error('Key verification failed:', verifyError);

      if (verifyError.message?.includes('No Treasury contract deployed')) {
        toast.error('❌ Treasury contract not found. Please check your network connection.');
      } else if (verifyError.message?.includes('Network error')) {
        toast.error('❌ Network error. Please check your blockchain connection.');
      } else {
        toast.error(`❌ Failed to verify keys: ${verifyError.message}`);
      }

      if (window.confirm(
        'Key verification failed. Do you want to load the keys anyway?\n\n' +
        'WARNING: This may result in attempting to use already spent keys.'
      )) {
        verifiedKeys.push(...keysData.availableKeys.map(key => ({ ...key, isUsed: false, verificationFailed: true })));
        toast.warning('⚠️ Keys loaded without blockchain verification. Use with caution.');
      } else {
        return false;
      }
    }

    // Convert to single-node withdrawal format
    const keysForWithdrawal = verifiedKeys.map(key => ({
      key_index: key.keyIndex, private_key: key.private_key,
      ethereum_address: key.ethereum_address, solana_address: key.solana_address,
      denomination: key.denomination, denomination_wei: key.denomination_wei,
      merkle_root: key.merkleRoot,
      merkle_root_id: key.merkleRootId ?? key.merkle_root_id ?? key.batchId ?? key.batch_id,
      chain_name: key.chain_name, chain_id: key.chain_id,
      token_symbol: key.token_symbol, token_address: key.token_address,
      treasury_address: key.treasury_address, merkle_proof: key.merkleProof,
      tree_index: key.treeIndex, batch_id: key.batchId,
      user_address: key.userAddress, isUsed: false
    }));

    // Merge with existing deposit if same txHash
    const existingDeposit = deposits.find(d => d.txHash === keysData.depositId);
    let finalKeys = keysForWithdrawal;
    let finalStatus = 'keys_received';
    let finalWithdrawals = [];

    if (existingDeposit) {
      console.log('📦 Found existing deposit, merging keys...');
      const existingKeyIndices = new Set((existingDeposit.keys || []).map(k => k.key_index || k.keyIndex));
      const newKeys = keysForWithdrawal.filter(k => !existingKeyIndices.has(k.key_index));
      finalKeys = [...(existingDeposit.keys || []), ...newKeys];
      finalStatus = existingDeposit.status || 'keys_received';
      finalWithdrawals = existingDeposit.withdrawals || [];
      console.log(`  Merged: ${existingDeposit.keys?.length || 0} existing + ${newKeys.length} new = ${finalKeys.length} total keys`);
    }

    setWithdrawDeposit({
      txHash: keysData.depositId, requestId: keysData.requestId,
      amount: verifiedKeys.reduce((sum, key) => sum + parseFloat(key.denomination), 0),
      keys: finalKeys, status: finalStatus, withdrawals: finalWithdrawals, isSingleNode: true
    });

    // Update deposit tracker
    const userAddr = account || solanaPublicKey?.toString() || '';
    const firstKey = verifiedKeys[0];
    depositTracker.ensureDeposit(
      keysData.depositId, userAddr,
      verifiedKeys.reduce((sum, key) => sum + parseFloat(key.denomination), 0),
      firstKey?.chain_id || null,
      { token: firstKey?.token_symbol || 'ETH', tokenAddress: firstKey?.token_address || '' }
    );
    depositTracker.updateDepositStatus(keysData.depositId, finalStatus, { keys: finalKeys, withdrawals: finalWithdrawals });

    setAvailableKeys(verifiedKeys);

    const tokenSymbol = verifiedKeys.length > 0 ? (verifiedKeys[0].token_symbol || verifiedKeys[0].tokenSymbol || 'ETH') : 'ETH';
    const totalAmount = verifiedKeys.reduce((sum, key) => sum + parseFloat(key.denomination), 0);
    toast.success(
      `✅ Successfully loaded ${verifiedKeys.length} verified unused key${verifiedKeys.length > 1 ? 's' : ''}! (Total value: ${totalAmount} ${tokenSymbol})`,
      { autoClose: 5000 }
    );
    return true;
  };

  // Process multi-node keyshare upload (legacy format)
  const processMultiNodeUpload = async (keysData) => {
    if (!keysData.keyshares || !keysData.depositId) {
      throw new Error('Invalid key file format');
    }
    if (typeof keysData.keyshares !== 'object' || Object.keys(keysData.keyshares).length === 0) {
      throw new Error('Invalid keyshares format - no keyshares found');
    }

    const hasValidKeyshares = Object.values(keysData.keyshares).some(nodeData =>
      nodeData && nodeData.keyshares && Array.isArray(nodeData.keyshares) && nodeData.keyshares.length > 0
    );
    if (!hasValidKeyshares) {
      throw new Error('Invalid keyshares format - no valid keyshares found');
    }

    // Ensure keys_metadata exists for all nodes
    Object.values(keysData.keyshares).forEach(nodeData => {
      if (!nodeData.keys_metadata) nodeData.keys_metadata = {};
    });

    console.log('Decrypted keys data:', keysData);

    if (keysData.isSingleKey && keysData.keyIndex !== undefined) {
      // Single key upload
      console.log('Loading single key:', keysData.keyIndex);

      if (withdrawDeposit && withdrawDeposit.txHash === keysData.depositId) {
        // Merge with existing keys
        const updatedKeyshares = { ...withdrawDeposit.keyshares };

        Object.entries(keysData.keyshares).forEach(([nodeId, nodeData]) => {
          if (!updatedKeyshares[nodeId]) {
            updatedKeyshares[nodeId] = { keyshares: [], keys_metadata: {} };
          }
          const existingIndex = updatedKeyshares[nodeId].keyshares.findIndex(
            ks => ks.key_index === keysData.keyIndex ||
              ks.key_index === String(keysData.keyIndex) ||
              ks.key_index === Number(keysData.keyIndex)
          );
          if (existingIndex >= 0) {
            updatedKeyshares[nodeId].keyshares[existingIndex] = nodeData.keyshares[0];
          } else {
            updatedKeyshares[nodeId].keyshares.push(nodeData.keyshares[0]);
          }
          const metadata = nodeData.keys_metadata[keysData.keyIndex] ||
            nodeData.keys_metadata[String(keysData.keyIndex)] ||
            nodeData.keys_metadata[Number(keysData.keyIndex)];
          if (metadata) updatedKeyshares[nodeId].keys_metadata[keysData.keyIndex] = metadata;
        });

        setWithdrawDeposit({ ...withdrawDeposit, keyshares: updatedKeyshares });

        const keysWithStatus = await withdrawalService.checkKeysStatus(provider, updatedKeyshares, keysData.depositId);
        setAvailableKeys(enrichKeysWithMetadata(keysWithStatus, keysData.availableKeys));
        toast.success(`Key ${keysData.keyIndex} loaded and merged successfully!`);
      } else {
        // Load single key as new
        setWithdrawDeposit({
          txHash: keysData.depositId, requestId: keysData.requestId,
          keyshares: keysData.keyshares,
          amount: keysData.availableKeys.reduce((sum, key) => sum + key.denomination, 0)
        });

        try {
          const keysWithStatus = await withdrawalService.checkKeysStatus(provider, keysData.keyshares, keysData.depositId);
          setAvailableKeys(enrichKeysWithMetadata(keysWithStatus, keysData.availableKeys));
        } catch (error) {
          console.error('Error checking key status:', error);
          setAvailableKeys(keysData.availableKeys);
        }

        toast.success(`Key ${keysData.keyIndex} loaded successfully!`);
      }
    } else {
      // Multiple keys upload
      setWithdrawDeposit({
        txHash: keysData.depositId, requestId: keysData.requestId,
        keyshares: keysData.keyshares,
        amount: keysData.availableKeys.reduce((sum, key) => sum + key.denomination, 0)
      });

      const keysWithStatus = await withdrawalService.checkKeysStatus(provider, keysData.keyshares, keysData.depositId);
      setAvailableKeys(enrichKeysWithMetadata(keysWithStatus, keysData.availableKeys));
      toast.success('All keys loaded successfully!');
    }
  };

  // ── Main upload orchestrator ────────────────────────────────────

  const uploadKeysWithPassword = async (password) => {
    try {
      const encryptedData = encryptedDataRef.current;
      encryptedDataRef.current = null;

      let keysData = KeyEncryptionService.decryptKeys(encryptedData, password);
      keysData = processUploadedKeys(keysData);

      if (keysData.isSingleNode) {
        if (await processSingleNodeUpload(keysData) === false) return;
      } else {
        await processMultiNodeUpload(keysData);
      }

      setWithdrawDialog(true);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to load keys: ' + error.message);
      throw error;
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: {
        bgcolor: 'rgba(255, 165, 2, 0.1)',
        color: '#ffa502',
        label: 'Pending'
      },
      confirmed: {
        bgcolor: 'rgba(0, 242, 254, 0.1)',
        color: '#22c55e',
        label: 'Confirmed'
      },
      keys_requested: {
        bgcolor: 'rgba(79, 172, 254, 0.1)',
        color: isDark ? '#a3a3a3' : '#525252',
        label: 'Keys Requested'
      },
      keys_received: {
        bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)',
        color: isDark ? '#e5e5e5' : '#171717',
        label: 'Keys Received'
      },
      withdrawn: {
        bgcolor: 'rgba(0, 230, 118, 0.1)',
        color: '#00e676',
        label: 'Unlocked'
      },
      failed: {
        bgcolor: 'rgba(255, 71, 87, 0.1)',
        color: '#ff4757',
        label: 'Failed'
      }
    };

    const config = statusConfig[status] || {
      bgcolor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
      color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
      label: status
    };

    return (
      <Chip
        size="small"
        label={config.label}
        sx={{
          bgcolor: config.bgcolor,
          color: config.color,
          border: `1px solid ${config.color}`,
          fontWeight: 600,
        }}
      />
    );
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const truncateHash = (hash) => {
    return `${hash.slice(0, 6)}...${hash.slice(-4)}`;
  };

  if (!connected && !solanaConnected && !tronConnected) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: { xs: 6, md: 12 },
          px: { xs: 2, md: 0 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}
      >
        <AccountBalanceWallet
          sx={{
            fontSize: { xs: 50, md: 80 },
            color: isDark ? '#e5e5e5' : '#171717',
            mb: 3,
            filter: isDark ? 'drop-shadow(0 8px 16px rgba(255, 255, 255, 0.1))' : 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.1))',
          }}
        />
        <Typography
          variant="h3"
          gutterBottom
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.5rem', md: undefined },
            background: isDark ? 'linear-gradient(90deg, #e5e5e5 0%, #737373 100%)' : 'linear-gradient(90deg, #171717 0%, #737373 100%)',
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
          }}
        >
          Connect Your Wallet
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            maxWidth: 500,
            mb: 4,
            textAlign: 'center',
            fontSize: { xs: '0.95rem', md: undefined },
          }}
        >
          Please connect your wallet using the buttons in the header to view your privacy dashboard
        </Typography>
        <Alert severity="info" sx={{ maxWidth: 500, mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>For EVM chains</strong> (Sepolia, BNB Testnet, Localhost):
          </Typography>
          <Typography variant="body2" sx={{ ml: 2, mb: 2 }}>
            Click the "EVM Wallet" button in the header to connect MetaMask
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>For Solana chains</strong> (Solana Devnet):
          </Typography>
          <Typography variant="body2" sx={{ ml: 2, mb: 2 }}>
            Click the "Select Wallet" button in the header to connect Phantom
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>For Tron chains</strong> (Tron Nile Testnet):
          </Typography>
          <Typography variant="body2" sx={{ ml: 2 }}>
            Click the "Connect" button next to TronLink in the header
          </Typography>
        </Alert>
      </Box>
    );
  }

  const accent = '#22c55e';

  return (
    <Box>

        {/* Header */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'flex-end' },
          justifyContent: 'space-between',
          gap: 3,
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'}`,
          pb: 3,
          mb: 3,
        }}>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                letterSpacing: '-0.03em',
                color: isDark ? '#e1e2eb' : '#171717',
                mb: 1,
                fontSize: { xs: '1.8rem', md: '2.4rem' },
              }}
            >
              Manage Keys
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  width: 8, height: 8,
                  bgcolor: dkgV2Ready ? '#22c55e' : '#ef4444',
                  boxShadow: dkgV2Ready ? '0 0 12px #22c55e' : '0 0 12px #ef4444',
                }} />
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: isDark ? '#b9cacb' : '#525252' }}>
                  DKG Nodes ({dkgNodeStatuses.filter(s => s.healthy && s.masterInitialized).length}/5):
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {dkgNodeStatuses.length > 0 ? (
                  dkgNodeStatuses.map((status, i) => (
                    <Typography
                      key={i}
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        color: status.healthy && status.masterInitialized
                          ? (isDark ? 'rgba(0, 219, 233, 0.8)' : '#16a34a')
                          : '#ef4444',
                      }}
                    >
                      Node {status.nodeId || i + 1}
                    </Typography>
                  ))
                ) : (
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: isDark ? '#525252' : '#a3a3a3' }}>
                    Checking...
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={14} /> : <Refresh />}
              onClick={() => { loadUserDeposits(); checkDkgNodes(); }}
              disabled={loading}
              sx={{
                borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)',
                color: isDark ? '#e1e2eb' : '#171717',
                fontWeight: 700,
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                px: 2.5, py: 1,
                '&:hover': {
                  borderColor: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.3)',
                  bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                },
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Upload />}
              onClick={handleUploadKeys}
              sx={{
                bgcolor: isDark ? '#22c55e' : '#16a34a',
                color: isDark ? '#0B0E14' : '#fff',
                fontWeight: 700,
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                px: 2.5, py: 1,
                boxShadow: isDark ? '0 0 20px rgba(34, 197, 94, 0.2)' : '0 4px 12px rgba(22, 163, 74, 0.3)',
                '&:hover': {
                  bgcolor: isDark ? '#22c55e' : '#15803d',
                },
              }}
            >
              Upload Keys
            </Button>
          </Box>
        </Box>

        {/* Wallet Status Bar */}
        {(account || solanaPublicKey) && (
          <Box sx={{
            bgcolor: isDark ? '#191c22' : '#f0f4f8',
            borderLeft: `4px solid ${isDark ? '#7000ff' : '#6d28d9'}`,
            p: 2,
            mb: 3,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { md: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AccountBalanceWallet sx={{ color: isDark ? '#7000ff' : '#6d28d9' }} />
              <Box>
                <Typography sx={{ fontSize: '0.6rem', textTransform: 'uppercase', fontWeight: 700, color: isDark ? '#525252' : '#a3a3a3', letterSpacing: '0.15em' }}>
                  Connected Wallet
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {account && (
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: isDark ? '#22c55e' : '#16a34a' }}>
                      {`${account.slice(0, 8)}...${account.slice(-8)}`}
                    </Typography>
                  )}
                  {solanaPublicKey && (
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: isDark ? '#d1bcff' : '#7c3aed' }}>
                      {`${solanaPublicKey.toString().slice(0, 6)}...${solanaPublicKey.toString().slice(-6)}`}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              px: 1.5, py: 0.5,
              bgcolor: isDark ? '#1D2026' : '#e8f4f8',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
            }}>
              <Box sx={{
                width: 6, height: 6, borderRadius: '50%',
                bgcolor: dkgV2Ready ? '#22c55e' : '#ef4444',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                },
              }} />
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', color: isDark ? '#22c55e' : '#16a34a' }}>
                {dkgV2Ready ? 'Network Stable' : 'Network Degraded'}
              </Typography>
            </Box>
          </Box>
        )}

      {/* Two Column Layout: Request Keys | Withdraw Keys */}
      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>

        {/* Left Column - Request Keys */}
        < Paper
          sx={{
            flex: 1,
            p: { xs: 2, md: 3 },
            background: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
            borderRadius: 3,
          }}
        >
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 600,
              mb: 2,
              color: isDark ? 'white' : '#171717',
            }}
          >
            Claim Keys
          </Typography>
          <Typography variant="body2" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)', mb: 2 }}>
            Locked funds awaiting key claims
          </Typography>

          {
            deposits.filter(d => d.status === 'confirmed' || d.status === 'keys_requested').length === 0 ? (
              <Alert severity="info">
                No locked funds awaiting key claims. All funds have keys or are unlocked.
              </Alert>
            ) : (
              <Stack spacing={2}>
                {(() => {
                  // Filter deposits awaiting key requests (confirmed or keys_requested)
                  const depositsAwaitingKeys = deposits.filter(d =>
                    d.status === 'confirmed' || d.status === 'keys_requested'
                  );

                  // Group deposits by chain
                  const depositsByChain = {};

                  depositsAwaitingKeys.forEach(deposit => {
                    let chainName;
                    let chainIdToLookup = null;

                    // Determine which ID to use (chainId takes priority for accuracy)
                    if (deposit.chainId) {
                      chainIdToLookup = deposit.chainId;
                    } else if (deposit.metadata?.targetChain) {
                      chainIdToLookup = deposit.metadata.targetChain;
                    }

                    // Now resolve the chainIdToLookup to a proper chain name
                    if (typeof chainIdToLookup === 'string') {
                      // String chainId (like 'solana-devnet') - use directly
                      chainName = chainIdToLookup;
                    } else if (typeof chainIdToLookup === 'number') {
                      // Numeric chainId - look it up in supportedChains from backend
                      const chain = supportedChains.find(c => c.chain_id === chainIdToLookup);
                      if (chain) {
                        chainName = chain.chain_name || chain.name;
                      } else {
                        // Fallback to manual mapping only if backend doesn't have it
                        chainName = getChainNameFromId(chainIdToLookup) || `Chain ${chainIdToLookup}`;
                      }
                    } else {
                      // No valid chain ID found
                      chainName = 'Unknown Chain';
                    }

                    // Fix for Hyperliquid if it comes as 'hyperliquid' string or specific ID
                    if (deposit.metadata?.targetChain === 'hyperliquid' || deposit.chainId === 999) {
                      chainName = 'hyperliquid';
                    }

                    if (!depositsByChain[chainName]) {
                      depositsByChain[chainName] = [];
                    }
                    depositsByChain[chainName].push(deposit);
                  });

                  return Object.entries(depositsByChain).map(([chainName, chainDeposits]) => {
                    const isChainExpanded = expandedChains[chainName];
                    const chainLogo = getChainLogo(chainName);

                    return (
                      <Card
                        key={chainName}
                        sx={{
                          background: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                          borderRadius: 2,
                          overflow: 'hidden',
                        }}
                      >
                        {/* Chain Header */}
                        <CardContent
                          onClick={() => toggleChain(chainName)}
                          sx={{
                            cursor: 'pointer',
                            p: 2,
                            bgcolor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                            '&:hover': {
                              bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              {chainLogo && (
                                <Box
                                  component="img"
                                  src={chainLogo}
                                  alt={chainName}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    objectFit: 'contain',
                                  }}
                                />
                              )}
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: isDark ? 'white' : '#171717' }}>
                                  {getChainDisplayName(chainName)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)' }}>
                                  {chainDeposits.length} deposit{chainDeposits.length !== 1 ? 's' : ''}
                                </Typography>
                              </Box>
                            </Box>
                            <ExpandMore
                              sx={{
                                transform: isChainExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s',
                                color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                              }}
                            />
                          </Box>
                        </CardContent>

                        {/* Chain Content - Deposits */}
                        <Collapse in={isChainExpanded}>
                          <CardContent sx={{ p: 2, pt: 0 }}>
                            <Stack spacing={1.5} sx={{ mt: 1 }}>
                              {chainDeposits.map((deposit) => {
                                const tokenSymbol = deposit.metadata?.token || 'ETH';
                                const tokenLogo = getTokenLogo(tokenSymbol);

                                return (
                                  <Card
                                    key={deposit.id}
                                    sx={{
                                      background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)',
                                      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                                      borderRadius: 1.5,
                                    }}
                                  >
                                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                      {/* Deposit Header */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {tokenLogo && (
                                            <Box
                                              component="img"
                                              src={tokenLogo}
                                              alt={tokenSymbol}
                                              sx={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: '50%',
                                                objectFit: 'contain',
                                              }}
                                            />
                                          )}
                                          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', color: isDark ? 'white' : '#171717' }}>
                                            {deposit.amount} {tokenSymbol}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                            {truncateHash(deposit.txHash)}
                                          </Typography>
                                        </Box>
                                        {getStatusChip(deposit.status)}
                                      </Box>

                                      {/* Remaining balance for partially claimed deposits */}
                                      {(() => {
                                        const actualClaimed = deposit.keys?.reduce((sum, k) => sum + parseFloat(k.denomination || 0), 0) || 0;
                                        const actualRemaining = Math.max(0, (deposit.amount || 0) - actualClaimed);
                                        if (actualRemaining <= 0.000001 || !deposit.keys?.length) return null;
                                        const claimPercent = (deposit.amount || 0) > 0 ? (actualClaimed / deposit.amount) * 100 : 0;
                                        return (
                                          <Box sx={{ mb: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                              <Typography variant="caption" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)', fontSize: '0.65rem' }}>
                                                {actualClaimed} claimed
                                              </Typography>
                                              <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 600, fontSize: '0.65rem' }}>
                                                {actualRemaining} remaining
                                              </Typography>
                                            </Box>
                                            <LinearProgress
                                              variant="determinate"
                                              value={claimPercent}
                                              sx={{
                                                height: 3,
                                                borderRadius: 2,
                                                bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                                                '& .MuiLinearProgress-bar': {
                                                  borderRadius: 2,
                                                  bgcolor: '#f59e0b',
                                                },
                                              }}
                                            />
                                          </Box>
                                        );
                                      })()}

                                      {/* Actions based on status */}
                                      {deposit.status === 'confirmed' && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                          {/* Withdraw — one-click DKG + relay */}
                                          <Button
                                            size="small"
                                            variant="contained"
                                            fullWidth
                                            startIcon={<AccountBalanceWallet />}
                                            onClick={() => handleWithdrawNow(deposit)}
                                            disabled={!dkgV2Ready}
                                            sx={{
                                              background: dkgV2Ready
                                                ? 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)'
                                                : 'rgba(100, 100, 100, 0.3)',
                                              boxShadow: dkgV2Ready ? '0 4px 12px rgba(34, 197, 94, 0.3)' : 'none',
                                              '&:hover': dkgV2Ready ? {
                                                background: 'linear-gradient(90deg, #16a34a 0%, #22c55e 100%)',
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 6px 16px rgba(34, 197, 94, 0.4)',
                                              } : {},
                                            }}
                                          >
                                            Unlock
                                          </Button>
                                          {/* Save Keys — request keys + download for later */}
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            fullWidth
                                            startIcon={<Download />}
                                            onClick={() => handleDkgWithdraw(deposit)}
                                            disabled={!dkgV2Ready}
                                            sx={{
                                              borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
                                              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                                              '&:hover': { borderColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' },
                                            }}
                                          >
                                            Save Keys
                                          </Button>
                                          {!dkgV2Ready && (
                                            <Typography variant="caption" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)', textAlign: 'center' }}>
                                              DKG nodes not ready
                                            </Typography>
                                          )}
                                        </Box>
                                      )}

                                      {deposit.status === 'keys_requested' && (
                                        <Box>
                                          <Typography variant="body2" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                                            Request ID: {deposit.keyRequestId?.slice(0, 8)}...
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)' }}>
                                            Keys pending... Check back soon or use Python scripts
                                          </Typography>
                                        </Box>
                                      )}
                                    </CardContent>
                                  </Card>
                                );
                              })}
                            </Stack>
                          </CardContent>
                        </Collapse>
                      </Card>
                    );
                  });
                })()}
              </Stack>
            )
          }
        </Paper >

        {/* Right Column - Withdraw Keys */}
        < Paper
          sx={{
            flex: 1,
            p: { xs: 2, md: 3 },
            background: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
            borderRadius: 3,
          }}
        >
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 600,
              mb: 2,
              color: isDark ? 'white' : '#171717',
            }}
          >
            Unlock Funds
          </Typography>
          <Typography variant="body2" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)', mb: 2 }}>
            Available keys ready to unlock
          </Typography>

          {(() => {
                  // Get all deposits with received keys (fully or partially claimed)
                  const depositsWithKeys = deposits.filter(d => d.status === 'keys_received' || (d.status === 'confirmed' && d.keys?.length > 0));

                  // Group individual keys by chain → token
                  const keysByChainAndToken = {};
                  let totalKeys = 0;

                  depositsWithKeys.forEach(deposit => {
                    const allKeys = deposit.keys || [];

                    // Show all keys — used keys show as "Retry", unused as "Unlock"
                    allKeys.forEach(key => {
                      totalKeys++;
                      const keyChainName = key.chain_name || 'Unknown';
                      const tokenSymbol = key.token_symbol || 'ETH';

                      if (!keysByChainAndToken[keyChainName]) {
                        keysByChainAndToken[keyChainName] = {};
                      }
                      if (!keysByChainAndToken[keyChainName][tokenSymbol]) {
                        keysByChainAndToken[keyChainName][tokenSymbol] = [];
                      }

                      keysByChainAndToken[keyChainName][tokenSymbol].push({
                        ...key,
                        // Ensure keyIndex is populated (handle both camelCase and snake_case)
                        keyIndex: key.keyIndex !== undefined ? key.keyIndex : key.key_index,
                        deposit: deposit, // Keep reference to parent deposit
                        timestamp: deposit.timestamp // Inherit timestamp from deposit for sorting
                      });
                    });
                  });

                  // Sort keys by timestamp (descending)
                  Object.keys(keysByChainAndToken).forEach(chain => {
                    Object.keys(keysByChainAndToken[chain]).forEach(token => {
                      keysByChainAndToken[chain][token].sort((a, b) => {
                        // Sort by timestamp descending (newest first)
                        const timeA = a.timestamp || 0;
                        const timeB = b.timestamp || 0;
                        if (timeA !== timeB) return timeB - timeA;

                        // Secondary sort by key index
                        return (a.keyIndex || 0) - (b.keyIndex || 0);
                      });
                    });
                  });

                  console.log('📊 Keys grouped by chain and token:', keysByChainAndToken);

                  if (totalKeys === 0) {
                    return (
                      <Alert severity="info">
                        No keys available yet. Claim keys from your locked funds first.
                      </Alert>
                    );
                  }

                  return (
                    <Stack spacing={2}>
                  {Object.entries(keysByChainAndToken).map(([chainName, tokenGroups]) => {
                    const isChainExpanded = expandedChains[`withdraw-${chainName}`];
                    const chainLogo = getChainLogo(chainName);
                    return (
                      <Card
                        key={chainName}
                        sx={{
                          background: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                          borderRadius: 2,
                          overflow: 'hidden',
                        }}
                      >
                        {/* Chain Header */}
                        <CardContent
                          onClick={() => toggleChain(`withdraw-${chainName}`)}
                          sx={{
                            cursor: 'pointer',
                            p: 2,
                            bgcolor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                            '&:hover': {
                              bgcolor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          {(() => {
                            const allKeys = Object.values(tokenGroups).flat();
                            const usedCount = allKeys.filter(k => k.isUsed).length;
                            const totalCount = allKeys.length;
                            const availableCount = totalCount - usedCount;
                            const totalLocked = allKeys
                              .filter(k => !k.isUsed)
                              .reduce((sum, k) => sum + parseFloat(k.denomination || 0), 0);
                            const tokenSymbol = Object.keys(tokenGroups)[0] || '';

                            return (
                              <>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: availableCount < totalCount ? 1.5 : 0 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    {chainLogo && (
                                      <Box
                                        component="img"
                                        src={chainLogo}
                                        alt={chainName}
                                        sx={{
                                          width: 32,
                                          height: 32,
                                          borderRadius: '50%',
                                          objectFit: 'contain',
                                        }}
                                      />
                                    )}
                                    <Box>
                                      <Typography variant="h6" sx={{ fontWeight: 600, color: isDark ? 'white' : '#171717' }}>
                                        {getChainDisplayName(chainName)}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)' }}>
                                        {availableCount} available{usedCount > 0 ? ` / ${usedCount} used` : ''} — {totalCount} total
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    {totalLocked > 0 && (
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#22c55e', fontFamily: 'monospace' }}>
                                        {totalLocked} {tokenSymbol}
                                      </Typography>
                                    )}
                                    <ExpandMore
                                      sx={{
                                        transform: isChainExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.3s',
                                        color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                                      }}
                                    />
                                  </Box>
                                </Box>
                                {usedCount > 0 && (
                                  <LinearProgress
                                    variant="determinate"
                                    value={(usedCount / totalCount) * 100}
                                    sx={{
                                      height: 4,
                                      borderRadius: 2,
                                      bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                                      '& .MuiLinearProgress-bar': {
                                        borderRadius: 2,
                                        bgcolor: usedCount === totalCount ? '#ef4444' : '#22c55e',
                                      },
                                    }}
                                  />
                                )}
                              </>
                            );
                          })()}
                        </CardContent>

                        {/* Chain Content - Token Groups */}
                        <Collapse in={isChainExpanded}>
                          <CardContent sx={{ p: 2, pt: 0 }}>
                            <Stack spacing={1.5} sx={{ mt: 1 }}>
                              {Object.entries(tokenGroups).map(([tokenSymbol, keys]) => {
                                const tokenKey = `withdraw-${chainName}-${tokenSymbol}`;
                                const isTokenExpanded = expandedTokens[tokenKey];
                                const tokenLogo = getTokenLogo(tokenSymbol);

                                return (
                                  <Card
                                    key={tokenSymbol}
                                    sx={{
                                      background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)',
                                      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                                      borderRadius: 1.5,
                                    }}
                                  >
                                    {/* Token Header */}
                                    <CardContent
                                      onClick={() => toggleToken(chainName, tokenSymbol)}
                                      sx={{
                                        cursor: 'pointer',
                                        p: 1.5,
                                        '&:last-child': { pb: 1.5 },
                                        '&:hover': {
                                          bgcolor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                                        },
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {tokenLogo && (
                                            <Box
                                              component="img"
                                              src={tokenLogo}
                                              alt={tokenSymbol}
                                              sx={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: '50%',
                                                objectFit: 'contain',
                                              }}
                                            />
                                          )}
                                          <Typography variant="body1" sx={{ fontWeight: 600, color: isDark ? 'white' : '#171717' }}>
                                            {tokenSymbol}
                                          </Typography>
                                          {(() => {
                                            const availableCount = keys.filter(k => !k.isUsed).length;
                                            const totalLocked = keys.filter(k => !k.isUsed).reduce((sum, k) => sum + parseFloat(k.denomination || 0), 0);
                                            return (
                                              <>
                                                <Chip
                                                  label={`${availableCount} available`}
                                                  size="small"
                                                  sx={{
                                                    bgcolor: availableCount > 0 ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                                    color: availableCount > 0 ? '#22c55e' : '#ef4444',
                                                    fontWeight: 600,
                                                    fontSize: '0.7rem',
                                                    height: 20,
                                                  }}
                                                />
                                                {totalLocked > 0 && (
                                                  <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 600, fontFamily: 'monospace' }}>
                                                    {totalLocked} {tokenSymbol}
                                                  </Typography>
                                                )}
                                              </>
                                            );
                                          })()}
                                        </Box>
                                        <ExpandMore
                                          sx={{
                                            transform: isTokenExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.3s',
                                            color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                                            fontSize: 20,
                                          }}
                                        />
                                      </Box>
                                    </CardContent>

                                    {/* Token Content - Individual Keys */}
                                    <Collapse in={isTokenExpanded}>
                                      <Box sx={{ px: 1.5, pb: 1.5 }}>
                                        <Stack spacing={1}>
                                          {keys.map((key, idx) => (
                                            <Card
                                              key={`${key.keyIndex}-${idx}`}
                                              sx={{
                                                background: key.isUsed
                                                  ? 'rgba(255, 82, 82, 0.05)'
                                                  : 'rgba(0, 230, 118, 0.05)',
                                                border: key.isUsed
                                                  ? '1px solid rgba(255, 82, 82, 0.2)'
                                                  : '1px solid rgba(0, 230, 118, 0.2)',
                                                borderRadius: 1.5,
                                              }}
                                            >
                                              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', color: isDark ? 'white' : '#171717' }}>
                                                      {key.denomination} {tokenSymbol}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)' }}>
                                                      #{key.keyIndex}
                                                    </Typography>
                                                  </Box>
                                                  <Chip
                                                    label={key.isUsed ? 'Used' : 'Available'}
                                                    size="small"
                                                    sx={{
                                                      bgcolor: key.isUsed ? 'rgba(239, 68, 68, 0.12)' : 'rgba(34, 197, 94, 0.12)',
                                                      color: key.isUsed ? '#ef4444' : '#22c55e',
                                                      fontWeight: 600,
                                                      fontSize: '0.7rem',
                                                      height: 20,
                                                    }}
                                                  />
                                                </Box>

                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                  <Button
                                                    size="small"
                                                    variant="contained"
                                                    color={key.isUsed ? "warning" : "primary"}
                                                    startIcon={<GetApp />}
                                                    onClick={() => handleWithdraw(key.deposit, key.keyIndex)}
                                                    sx={{
                                                      background: key.isUsed
                                                        ? 'rgba(255, 152, 0, 0.2)'
                                                        : (isDark ? '#ffffff' : '#171717'),
                                                      color: key.isUsed ? '#ff9800' : (isDark ? '#050505' : '#ffffff'),
                                                      border: key.isUsed ? '1px solid rgba(255, 152, 0, 0.5)' : 'none',
                                                      fontSize: '0.75rem',
                                                      px: 1.5,
                                                      py: 0.5,
                                                      flex: 1,
                                                      whiteSpace: 'nowrap',
                                                      '&:hover': {
                                                        background: key.isUsed
                                                          ? 'rgba(255, 152, 0, 0.3)'
                                                          : (isDark ? '#d4d4d4' : '#404040'),
                                                      },
                                                    }}
                                                  >
                                                    {key.isUsed ? 'Retry' : 'Unlock'}
                                                  </Button>
                                                  <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<Download />}
                                                    onClick={() => handleDownloadKeys(key.keyIndex, key.deposit)}
                                                    sx={{
                                                      borderColor: isDark ? '#262626' : '#e5e5e5',
                                                      color: isDark ? '#a3a3a3' : '#525252',
                                                      fontSize: '0.75rem',
                                                      px: 1.5,
                                                      py: 0.5,
                                                      flex: 1,
                                                      whiteSpace: 'nowrap',
                                                      '&:hover': {
                                                        borderColor: isDark ? '#525252' : '#a3a3a3',
                                                        bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)',
                                                      },
                                                    }}
                                                  >
                                                    Save Key
                                                  </Button>
                                                </Box>
                                              </CardContent>
                                            </Card>
                                          ))}
                                        </Stack>
                                      </Box>
                                    </Collapse>
                                  </Card>
                                );
                              })}
                            </Stack>
                          </CardContent>
                        </Collapse>
                      </Card>
                    );
                  })}
                    </Stack>
                  );
                })()}
        </Paper >
      </Box >

      <KeyRequestDialog
        open={keyRequestDialog}
        onClose={() => setKeyRequestDialog(false)}
        selectedDeposit={selectedDeposit}
        sourceChain={sourceChain}
        supportedChains={supportedChains}
        denominationRequests={denominationRequests}
        availableTokens={availableTokens}
        keyRequestLoading={keyRequestLoading}
        onUpdateRequest={updateDenominationRequest}
        onAddRequest={addDenominationRequest}
        onRemoveRequest={removeDenominationRequest}
        onExecute={executeKeyRequest}
        isMobile={isMobile}
      />

      {/* Withdraw Now — one-click DKG + relay */}
      <WithdrawNowDialog
        open={withdrawNowDialog}
        onClose={() => { setWithdrawNowDialog(false); setWithdrawNowDeposit(null); }}
        deposit={withdrawNowDeposit}
        dkgNodeStatuses={dkgNodeStatuses}
        dkgV2Ready={dkgV2Ready}
        dkgDenominations={dkgDenominations}
        supportedChains={supportedChains}
        validTargets={getValidWithdrawalTargets(
          withdrawNowDeposit?.metadata?.token || 'ETH',
          sourceChain
        )}
        mappingsLoading={mappingsLoading}
        onExecute={executeWithdrawNow}
        isDark={isDark}
        isMobile={isMobile}
      />

      {/* Save Keys — request keys only (existing DKG dialog) */}
      <DkgRequestDialog
        open={dkgRequestDialog}
        onClose={() => setDkgRequestDialog(false)}
        dkgSelectedDeposit={dkgSelectedDeposit}
        dkgNodeStatuses={dkgNodeStatuses}
        dkgV2Ready={dkgV2Ready}
        dkgDenominations={dkgDenominations}
        dkgWithdrawalRequests={dkgWithdrawalRequests}
        dkgWithdrawalProgress={dkgWithdrawalProgress}
        keyRequestLoading={keyRequestLoading}
        supportedChains={supportedChains}
        validTargets={getValidWithdrawalTargets(
          dkgSelectedDeposit?.metadata?.token || 'ETH',
          sourceChain
        )}
        mappingsLoading={mappingsLoading}
        onUpdateRequest={updateDkgWithdrawalRequest}
        onAddRequest={addDkgWithdrawalRequest}
        onRemoveRequest={removeDkgWithdrawalRequest}
        onExecute={executeDkgWithdrawal}
        isDark={isDark}
        isMobile={isMobile}
      />

      <WithdrawalDialog
        open={withdrawDialog}
        onClose={() => { setWithdrawDialog(false); setRelayJobId(null); setRelayNodeUrl(null); }}
        withdrawDeposit={withdrawDeposit}
        availableKeys={availableKeys}
        selectedKeyIndex={selectedKeyIndex}
        onSelectKey={setSelectedKeyIndex}
        recipientAddress={recipientAddress}
        onRecipientChange={setRecipientAddress}
        withdrawalMode={withdrawalMode}
        onWithdrawalModeChange={setWithdrawalMode}
        relayInfo={relayInfo}
        relayerHasBalance={relayerHasBalance}
        withdrawing={withdrawing}
        onExecuteWithdrawal={executeWithdrawal}
        relayJobId={relayJobId}
        relayNodeUrl={relayNodeUrl}
        expandedChains={expandedChains}
        expandedTokens={expandedTokens}
        onToggleChain={toggleChain}
        onToggleToken={toggleToken}
        onDownloadKeys={handleDownloadKeys}
        onUploadKeys={handleUploadKeys}
        isDark={isDark}
        isMobile={isMobile}
        account={account}
        solanaPublicKey={solanaPublicKey}
        solanaConnected={solanaConnected}
      />


      {/* Hidden file input for uploading keys */}
      < input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".enc,.json"
        style={{ display: 'none' }}
      />

      {/* Password dialog for encryption/decryption */}
      <PasswordDialog
        open={passwordDialog}
        onClose={() => {
          setPasswordDialog(false);
          setDownloadKeyIndex(null);
          encryptedDataRef.current = null;
        }}
        onConfirm={passwordDialogMode === 'download' ? downloadKeysWithPassword : uploadKeysWithPassword}
        title={
          passwordDialogMode === 'download'
            ? (downloadKeyIndex !== null ? `Encrypt Key ${downloadKeyIndex} for Saving` : 'Encrypt All Keys for Saving')
            : 'Decrypt Keys from File'
        }
        description={
          passwordDialogMode === 'download'
            ? (downloadKeyIndex !== null
              ? `Enter a password to encrypt key ${downloadKeyIndex}. You will need this password to decrypt the key later.`
              : 'Enter a password to encrypt all keys. You will need this password to decrypt the keys later.')
            : 'Enter the password used to encrypt this key file.'
        }
        confirmButtonText={passwordDialogMode === 'download' ? 'Save' : 'Load Keys'}
      />

      <ErrorDialog
        open={errorDialog}
        onClose={() => setErrorDialog(false)}
        errorDetails={errorDetails}
        isDark={isDark}
        isMobile={isMobile}
      />

      <SuccessDialog
        open={successDialog}
        onClose={() => { setSuccessDialog(false); setWithdrawDialog(false); setRelayJobId(null); setRelayNodeUrl(null); }}
        successDetails={successDetails}
        isDark={isDark}
        isMobile={isMobile}
      />
    </Box>
  );
};

export default Dashboard;